import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { 
  Sparkle, VideoCamera, Package, Browser, Envelope, 
  Copy, CheckCircle, Eye, CaretDown, CaretUp, FilmSlate,
  ArrowLeft, FolderOpen, MagnifyingGlass,
  Lightning, ArrowRight, Star, PenNib, 
  SortAscending, X, ArrowsClockwise, Link, Target, Files, Check,
  Spinner, ArrowSquareOut, Download, Trash, SpeakerHigh, SpeakerSlash,
  ShareNetwork, PaperPlaneTilt, PencilSimple as PencilIcon
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api, { videoAPI } from '@/utils/api';
import { getUser, isAuthenticated } from '@/utils/auth';
import BulkVideoModal from '@/components/BulkVideoModal';
import FloatingHelpButton from '@/components/FloatingHelpButton';
import { BusinessPackageCard } from '@/components/AssetThumbnailCard';
import { downloadBlob, isIOS } from '@/utils/downloadBlob';
// Round 79 — single source of truth for video readiness across all surfaces.
import { NEXT_ACTION, getVideoNextAction } from '@/components/shared/videoWorkflow';
// Round 58 — permanent source-of-truth detail view that reopens the
// complete saved asset + all export artifacts (PDF/HTML/video).
import CreationDetailModal from '@/components/CreationDetailModal';
// Round 61 — shared card-level action buttons (View / Download / Copy / Delete).
import DeliverableActions from '@/components/DeliverableActions';

const MyCreationsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = getUser();
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);
  
  // PART 10: Video creation state for My Creations
  const [isCreatingVideo, setIsCreatingVideo] = useState(false);
  const [videoCreationJobId, setVideoCreationJobId] = useState(null);
  
  // PART 3 & 4: Video credits state
  const [videoCredits, setVideoCredits] = useState(0);
  
  // Internal account detection using existing user from getUser()
  const isInternalAccount = user?.is_internal || user?.plan_tier === 'internal' || user?.role === 'admin';

  // Round 58 — Reopen-full state. Any descendant can call
  // `window.dispatchEvent(new CustomEvent('paygbot:open-creation', {detail: id}))`
  // to pop the detail modal, or pass `onPreview` to BusinessPackageCard.
  // Round 61 — also listen for 'paygbot:refresh-creations' so card-
  // level deletes (DeliverableActions Trash) refresh the list inline
  // without forcing the user to reopen the modal.
  const [reopenId, setReopenId] = useState(null);
  useEffect(() => {
    const openHandler = (e) => {
      if (e?.detail) setReopenId(e.detail);
    };
    const refreshHandler = () => {
      if (typeof fetchAllData === 'function') fetchAllData();
    };
    window.addEventListener('paygbot:open-creation', openHandler);
    window.addEventListener('paygbot:refresh-creations', refreshHandler);
    return () => {
      window.removeEventListener('paygbot:open-creation', openHandler);
      window.removeEventListener('paygbot:refresh-creations', refreshHandler);
    };
  }, []);

  // Phase 4.2 — Founder-only "Retry with v2" state.
  // `v2EnvAvailable=true` only when backend reports the env flag is ON
  // AND ElevenLabs+Runway keys are configured. Drives whether the
  // founder sees the "Retry with v2" button at all.
  const [v2EnvAvailable, setV2EnvAvailable] = useState(false);
  const [retryV2JobId, setRetryV2JobId] = useState(null);
  const [retryV2SourceId, setRetryV2SourceId] = useState(null);
  
  // Bulk video generation state
  const [showBulkVideoModal, setShowBulkVideoModal] = useState(false);
  const [bulkVideoItems, setBulkVideoItems] = useState([]);
  const [bulkVideoPackTitle, setBulkVideoPackTitle] = useState('');
  const [isCreatingBulk, setIsCreatingBulk] = useState(false);
  
  // Data states
  const [content, setContent] = useState([]);
  const [videos, setVideos] = useState([]);
  const [offers, setOffers] = useState([]);
  const [salesPages, setSalesPages] = useState([]);
  const [emailCampaigns, setEmailCampaigns] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [scoredContent, setScoredContent] = useState([]);
  const [contentPacks, setContentPacks] = useState([]);
  const [linkInBio, setLinkInBio] = useState([]);
  const [businessPlans, setBusinessPlans] = useState([]);
  const [dfyVideos, setDfyVideos] = useState([]);
  const [businessPackages, setBusinessPackages] = useState([]);
  const [ebooks, setEbooks] = useState([]);
  
  // Expanded states
  const [expandedItems, setExpandedItems] = useState({});
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    fetchAllData();
    fetchVideoCredits();
    // Round 57 — refresh the cached user via /auth/me so flags like
    // `is_internal` are always current. The frontend's
    // `isInternalAccount` check reads from localStorage which can be
    // stale (e.g. after the founder DB row was healed but the user
    // hasn't re-logged in). The /auth/me handler self-heals the
    // founder row server-side AND returns the patched user, which we
    // then write back to localStorage.
    (async () => {
      try {
        const me = await api.get('/auth/me');
        if (me?.data) {
          // Mirror the canonical user shape used at login time.
          localStorage.setItem('user', JSON.stringify(me.data));
        }
      } catch (_e) {
        // Non-fatal — interceptor handles 401, anything else just
        // leaves the stale cached user in place.
      }
    })();
    
    // PART 7: Check for purchase success from URL params
    if (searchParams.get('video_credits_purchased') === 'true') {
      const bundle = searchParams.get('bundle');
      toast.success(`${bundle || ''} video credits added to your account!`.trim());
      // Clear the URL params
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Phase 4.2 — Pull v2 env-flag status once on mount. Founder-only
    // endpoint; non-founder calls return 403 which we swallow and
    // leave `v2EnvAvailable=false` (the Retry-with-v2 button stays
    // hidden). This call is the SAME `/api/v2-demo/status` the
    // Content Bot modal uses.
    (async () => {
      try {
        const r = await api.get('/v2-demo/status');
        setV2EnvAvailable(!!r?.data?.real_providers_available);
      } catch (_e) {
        setV2EnvAvailable(false);
      }
    })();
  }, []);
  
  // PART 3: Fetch video credit balance
  // Round 58 — log the values so production bugs ("Stripe checkout
  // fired despite 99,999 credits") are diagnosable from the browser
  // console. The button-side log lives in handleCreateVideoFromContent.
  const fetchVideoCredits = async () => {
    try {
      const response = await videoAPI.getCredits();
      const balance = response.data?.video_credits ?? 0;
      const internal = response.data?.is_internal === true;
      setVideoCredits(balance);
      console.info(
        `[VideoCredits:MyCreations] balance=${balance} is_internal=${internal} `
        + `can_create_video=${response.data?.can_create_video} source=/video-credits/balance`
      );
    } catch (error) {
      console.error('Failed to fetch video credits:', error);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [contentRes, offersRes, pagesRes, emailsRes, creationsRes, myCreationsRes] = await Promise.all([
        api.get('/content/recent?limit=100'),
        api.get('/monetization/offers?limit=50'),
        api.get('/monetization/sales-pages?limit=50'),
        api.get('/monetization/email-campaigns?limit=50'),
        api.get('/creations/all?limit=100'),
        api.get('/my-creations').catch(() => ({ data: { creations: [] } }))
      ]);

      // Separate content by type
      const allContent = contentRes.data || [];
      const regularContent = allContent.filter(item => !item.type || item.type === 'content');
      const videoContent = allContent.filter(item => item.script);
      const hookContent = allContent.filter(item => item.type === 'hook_improvement');
      const scored = allContent.filter(item => item.type === 'content_score');
      const packs = allContent.filter(item => item.type === 'content_pack');

      // Combine creations from both endpoints
      const oldCreations = creationsRes.data || [];
      const newCreations = myCreationsRes.data?.creations || [];
      const allCreations = [...oldCreations, ...newCreations];
      const businessPlanCreations = allCreations.filter(item => item.type === 'business_plan' || item.asset_type === 'business_plan');
      const contentPackCreations = allCreations.filter(item => item.type === 'content_pack' || item.asset_type === 'content_pack');
      const linkInBioCreations = allCreations.filter(item => item.type === 'link_in_bio' || item.asset_type === 'link_in_bio');
      const dfyVideoCreations = allCreations.filter(item => item.type === 'dfy_video' || item.asset_type === 'dfy_video');
      const businessPackageCreations = allCreations.filter(item => item.type === 'business_package' || item.asset_type === 'business_package');
      const ebookCreations = allCreations.filter(item => item.type === 'ebook' || item.asset_type === 'ebook');

      setContent(regularContent);
      setVideos(videoContent);
      setHooks(hookContent);
      setScoredContent(scored);
      setOffers(offersRes.data || []);
      setSalesPages(pagesRes.data || []);
      setEmailCampaigns(emailsRes.data || []);
      setContentPacks([...packs, ...contentPackCreations]);
      setLinkInBio(linkInBioCreations);
      setBusinessPlans(businessPlanCreations);
      setDfyVideos(dfyVideoCreations);
      setBusinessPackages(businessPackageCreations);
      setEbooks(ebookCreations);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load creations');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const toggleExpanded = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Get all items for the "All" tab
  const getAllItems = () => {
    const all = [
      ...content.map(item => ({ ...item, _type: 'content' })),
      ...offers.map(item => ({ ...item, _type: 'offer' })),
      ...salesPages.map(item => ({ ...item, _type: 'sales-page' })),
      ...emailCampaigns.map(item => ({ ...item, _type: 'email' })),
      ...hooks.map(item => ({ ...item, _type: 'hook' })),
      ...scoredContent.map(item => ({ ...item, _type: 'scored' })),
      ...contentPacks.map(item => ({ ...item, _type: 'content-pack' })),
      ...linkInBio.map(item => ({ ...item, _type: 'link-in-bio' })),
      ...businessPlans.map(item => ({ ...item, _type: 'business-plan' })),
      ...dfyVideos.map(item => ({ ...item, _type: 'dfy-video' })),
      ...businessPackages.map(item => ({ ...item, _type: 'business-package' })),
      ...ebooks.map(item => ({ ...item, _type: 'ebook' }))
    ];
    return sortItems(all);
  };

  // Sort items
  const sortItems = (items) => {
    return [...items].sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });
  };

  const tabs = [
    { id: 'all', label: 'All', icon: FolderOpen, count: getAllItems().length },
    { id: 'business-packages', label: 'Packages', icon: Package, count: businessPackages.length },
    { id: 'business-plans', label: 'Plans', icon: Target, count: businessPlans.length },
    { id: 'content', label: 'Content', icon: Sparkle, count: content.length },
    { id: 'content-packs', label: 'Packs', icon: Lightning, count: contentPacks.length },
    { id: 'dfy-videos', label: 'Videos', icon: VideoCamera, count: dfyVideos.length },
    { id: 'offers', label: 'Offers', icon: Package, count: offers.length },
    { id: 'sales-pages', label: 'Sales', icon: Browser, count: salesPages.length },
    { id: 'emails', label: 'Emails', icon: Envelope, count: emailCampaigns.length },
    { id: 'link-in-bio', label: 'Links', icon: Link, count: linkInBio.length },
    { id: 'ebooks', label: 'Ebooks', icon: Files, count: ebooks.length }
  ];

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatRelativeDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return formatDate(dateStr);
  };

  // PART 10: Create video from content item in My Creations
  const handleCreateVideoFromContent = async (item) => {
    if (!item.video_prompt || !item.script) {
      toast.error('This content is missing video prompt or script');
      return;
    }

    // Round 58 (prod-resilience): NEVER trust stale localStorage for
    // the founder/internal gate. Re-check with the backend right
    // before deciding whether to open the purchase modal. This
    // eliminates the production failure where founder@paygbot.com
    // saw the customer paywall because localStorage was cached
    // pre-deploy with is_internal=false.
    let backendInternal = false;
    let backendBalance = videoCredits;
    try {
      const fresh = await videoAPI.getCredits();
      backendInternal = fresh?.data?.is_internal === true;
      backendBalance = fresh?.data?.video_credits ?? 0;
      setVideoCredits(backendBalance);
    } catch (e) {
      // Network blip — fall back to whatever we have in state.
      console.warn('[CreateVideo:MyCreations] balance refresh failed, falling back to cached state', e);
    }
    console.info(
      `[CreateVideo:MyCreations] click — cachedCredits=${videoCredits} `
      + `backendCredits=${backendBalance} cachedInternal=${isInternalAccount} `
      + `backendInternal=${backendInternal} `
      + `gate=${(backendInternal || isInternalAccount) ? 'BYPASS' : (backendBalance <= 0 ? 'OPEN_PURCHASE_MODAL' : 'PROCEED')}`
    );
    const allowBypass = backendInternal || isInternalAccount;
    if (!allowBypass && backendBalance <= 0) {
      /* Phase G — no video credits modal */
      return;
    }
    
    setIsCreatingVideo(true);
    setVideoCreationJobId(item.id);
    
    try {
      // Round 79 — Shared voice-first gate is enforced by /videos/create.
      // We must preview the voiceover first (free if cached by hash) and
      // pass voice_preview through. Mirrors GenerateContentModal exactly.
      let voicePreview = item.voice_preview || null;
      if (!voicePreview?.audio_url || !voicePreview?.audio_duration) {
        const cleanScript = (item.script || '').trim();
        if (!cleanScript) {
          toast.error('Script is required to generate a video.');
          setIsCreatingVideo(false);
          setVideoCreationJobId(null);
          return;
        }
        const previewResp = await videoAPI.previewVoiceover({ script: cleanScript });
        voicePreview = {
          audio_url: previewResp.data.audio_url,
          audio_duration: previewResp.data.audio_duration,
          script_hash: previewResp.data.script_hash,
          cached: Boolean(previewResp.data.cached),
          previewed_at: new Date().toISOString(),
        };
        toast.success(
          previewResp.data.cached
            ? `Using cached voiceover (free) — ${voicePreview.audio_duration?.toFixed?.(1) || '?'}s.`
            : `Voiceover ready — ${voicePreview.audio_duration?.toFixed?.(1) || '?'}s.`,
        );
      }

      // Create video job using the API (now passes voice_preview)
      const response = await videoAPI.createVideo({
        title: item.title || 'Content Video',
        script: item.script,
        video_prompt: item.video_prompt,
        // PART 8 & 3: Pass explicit hook for proper segmentation
        hook: item.selected_hook || null,
        cta: item.cta || null,
        content_id: item.id,
        voice_preview: voicePreview,
      });
      
      const jobId = response.data.job_id;
      toast.success('Video generation started!');
      
      // Refresh credit balance after starting job
      fetchVideoCredits();
      
      // Poll for status
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await videoAPI.getVideoStatus(jobId);
          const status = statusResponse.data.status;
          
          if (status === 'completed') {
            clearInterval(pollInterval);
            setIsCreatingVideo(false);
            setVideoCreationJobId(null);
            toast.success('Your video is ready! Check the Videos tab.');
            // Refresh data to show new video
            fetchAllData();
          } else if (status === 'failed') {
            clearInterval(pollInterval);
            setIsCreatingVideo(false);
            setVideoCreationJobId(null);
            const errRaw = statusResponse?.data?.error_message;
            const errMsg = typeof errRaw === 'string'
              ? errRaw
              : (errRaw && typeof errRaw === 'object' && errRaw.message)
                ? errRaw.message
                : 'Video generation failed';
            toast.error(errMsg);
          }
        } catch (error) {
          console.error('Error polling video status:', error);
        }
      }, 5000);
      
      // Timeout after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (isCreatingVideo) {
          setIsCreatingVideo(false);
          setVideoCreationJobId(null);
          toast.error('Video generation timed out');
        }
      }, 300000);
      
    } catch (error) {
      console.error('Error creating video:', error);
      // Round 56 — backend returns 402 with `detail` as an OBJECT
      // ({message, video_credits, purchase_required}) when out of
      // credits. Passing that object to sonner's `toast.error(...)`
      // tries to render it as a React child and crashes the tree
      // (objects are not valid React children) — that's what was
      // producing the blank page on Create Video. Coerce to a string
      // before showing the toast, and route insufficient-credits
      // errors to the purchase modal so the user has a path forward.
      const detailRaw = error?.response?.data?.detail;
      const detailMsg =
        typeof detailRaw === 'string'
          ? detailRaw
          : detailRaw && typeof detailRaw === 'object'
          ? (detailRaw.message || detailRaw.error || 'Failed to start video generation')
          : 'Failed to start video generation';
      const isInsufficientCredits =
        error?.response?.status === 402
        || (detailRaw && typeof detailRaw === 'object' && detailRaw.purchase_required === true);
      if (isInsufficientCredits) {
        /* Phase G — no video credits modal */
      } else {
        toast.error(detailMsg);
      }
      setIsCreatingVideo(false);
      setVideoCreationJobId(null);
    }
  };

  // Phase 4.2 — Founder-only "Retry with v2" handler. Posts to the
  // new `/api/videos/retry-v2/{content_id}` endpoint, then polls
  // `/api/videos/v2-status/{job_id}` until done. On success a NEW
  // generated_content row is created (the source row is preserved
  // even on failure — that's the Phase 4.2 safety contract).
  //
  // Bails out early with a clear toast if the backend env flag is
  // OFF instead of submitting and getting a 409 mid-flow.
  const handleRetryWithV2 = async (item) => {
    if (!isInternalAccount) return;  // belt-and-braces; UI hides this for clients
    if (!v2EnvAvailable) {
      toast.error(
        'v2 retry disabled — set VIDEO_ENGINE_V2_REAL_PROVIDERS=1 on the backend and restart.'
      );
      return;
    }
    if (retryV2JobId) {
      toast.error('A v2 retry is already running.');
      return;
    }
    setRetryV2JobId('starting');
    setRetryV2SourceId(item.id);
    try {
      const resp = await api.post(`/videos/retry-v2/${item.id}`, {});
      const jobId = resp?.data?.job_id;
      if (!jobId) throw new Error('v2 retry returned no job_id');
      setRetryV2JobId(jobId);
      toast.success('v2 retry queued — ElevenLabs + Runway running in the background.');

      const pollInterval = setInterval(async () => {
        try {
          const s = await api.get(`/videos/v2-status/${jobId}`);
          const status = s?.data?.status;
          if (status === 'completed') {
            clearInterval(pollInterval);
            setRetryV2JobId(null);
            setRetryV2SourceId(null);
            toast.success('v2 retry complete — new voiced video saved to My Projects.');
            fetchAllData();
          } else if (status === 'failed') {
            clearInterval(pollInterval);
            setRetryV2JobId(null);
            setRetryV2SourceId(null);
            const code = s?.data?.error_code || 'engine_failed';
            const msg = s?.data?.error_message || 'v2 retry failed (no message).';
            toast.error(`v2 retry failed (${code}): ${msg}`, { duration: 8000 });
          }
        } catch (e) {
          // Status fetch hiccup — keep polling. Final timeout fires after 5 min below.
          console.warn('[RetryV2] status poll error', e);
        }
      }, 5000);

      // Hard 5-minute timeout — even Runway gen4.5 + freeze-frame mux
      // is well under 4 min for our scripts. If we exceed 5 min, the
      // backend worker is stuck and we surface that to the founder.
      setTimeout(() => {
        clearInterval(pollInterval);
        if (retryV2JobId === jobId) {
          setRetryV2JobId(null);
          setRetryV2SourceId(null);
          toast.error('v2 retry timed out after 5 min — check backend logs.');
        }
      }, 300000);
    } catch (e) {
      setRetryV2JobId(null);
      setRetryV2SourceId(null);
      const detail = e?.response?.data?.detail || e?.message || 'unknown';
      toast.error(`v2 retry could not start: ${detail}`);
    }
  };


  // Handler for bulk video creation
  const handleCreateBulkVideos = (contentItems, packTitle) => {
    setBulkVideoItems(contentItems);
    setBulkVideoPackTitle(packTitle);
    setShowBulkVideoModal(true);
  };

  // 2026-05-31 — Delete a project (DFY video or any other My Creations
  // item). Backend now scans BOTH `my_creations` and `generated_content`
  // and applies an owner check unless the caller is founder/admin.
  // Confirmation lives in the per-card modal so the user always gets a
  // chance to back out.
  const handleDeleteCreation = async (creationId, displayTitle) => {
    if (!creationId) return;
    try {
      await api.delete(`/my-creations/${creationId}`);
      toast.success(displayTitle ? `Deleted "${displayTitle}"` : 'Project deleted');
      // Optimistic refresh — the deleted card disappears immediately.
      fetchAllData();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to delete project';
      toast.error(detail);
    }
  };

  // Filter items based on search
  const filterItems = (items, fields) => {
    if (!searchQuery.trim()) return sortItems(items);
    const query = searchQuery.toLowerCase();
    return sortItems(items.filter(item => 
      fields.some(field => item[field]?.toString().toLowerCase().includes(query))
    ));
  };

  // Render item based on type for "All" tab
  // Use composite key (type + id) to avoid collisions between different collections
  const renderAllItem = (item) => {
    const uniqueKey = `${item._type}-${item.id}`;
    const expandKey = `${item._type}-${item.id}`;
    switch (item._type) {
      case 'content':
        return <ContentCard key={uniqueKey} item={item} expanded={expandedItems[expandKey]} onToggle={() => toggleExpanded(expandKey)} onCopy={handleCopy} copiedField={copiedField} formatDate={formatRelativeDate} onCreateVideo={handleCreateVideoFromContent} isCreatingVideo={isCreatingVideo} videoCreationJobId={videoCreationJobId} isInternalAccount={isInternalAccount} />;
      case 'offer':
        return <OfferCard key={uniqueKey} item={item} expanded={expandedItems[expandKey]} onToggle={() => toggleExpanded(expandKey)} onCopy={handleCopy} copiedField={copiedField} formatDate={formatRelativeDate} />;
      case 'sales-page':
        return <SalesPageCard key={uniqueKey} item={item} expanded={expandedItems[expandKey]} onToggle={() => toggleExpanded(expandKey)} onCopy={handleCopy} copiedField={copiedField} formatDate={formatRelativeDate} />;
      case 'email':
        return <EmailCampaignCard key={uniqueKey} item={item} expanded={expandedItems[expandKey]} onToggle={() => toggleExpanded(expandKey)} onCopy={handleCopy} copiedField={copiedField} formatDate={formatRelativeDate} />;
      case 'hook':
        return <HookCard key={uniqueKey} item={item} expanded={expandedItems[expandKey]} onToggle={() => toggleExpanded(expandKey)} onCopy={handleCopy} copiedField={copiedField} formatDate={formatRelativeDate} />;
      case 'scored':
        return <ScoredContentCard key={uniqueKey} item={item} expanded={expandedItems[expandKey]} onToggle={() => toggleExpanded(expandKey)} onCopy={handleCopy} copiedField={copiedField} formatDate={formatRelativeDate} />;
      case 'content-pack':
        return <ContentPackCard key={uniqueKey} item={item} expanded={expandedItems[expandKey]} onToggle={() => toggleExpanded(expandKey)} onCopy={handleCopy} copiedField={copiedField} formatDate={formatRelativeDate} onCreateBulkVideos={handleCreateBulkVideos} isCreatingBulk={isCreatingBulk} isInternalAccount={isInternalAccount} />;
      case 'link-in-bio':
        return <LinkInBioCard key={uniqueKey} item={item} expanded={expandedItems[expandKey]} onToggle={() => toggleExpanded(expandKey)} onCopy={handleCopy} copiedField={copiedField} formatDate={formatRelativeDate} />;
      case 'business-plan':
        return <BusinessPlanCard key={uniqueKey} item={item} expanded={expandedItems[expandKey]} onToggle={() => toggleExpanded(expandKey)} onCopy={handleCopy} copiedField={copiedField} formatDate={formatRelativeDate} />;
      case 'dfy-video':
        return <DfyVideoCard key={uniqueKey} item={item} expanded={expandedItems[expandKey]} onToggle={() => toggleExpanded(expandKey)} onCopy={handleCopy} copiedField={copiedField} formatDate={formatRelativeDate} isInternalAccount={isInternalAccount} v2EnvAvailable={v2EnvAvailable} onRetryV2={handleRetryWithV2} retryV2JobId={retryV2JobId} retryV2SourceId={retryV2SourceId} onDelete={handleDeleteCreation} />;
      case 'ebook':
        return <EbookCard key={uniqueKey} item={item} expanded={expandedItems[expandKey]} onToggle={() => toggleExpanded(expandKey)} onCopy={handleCopy} copiedField={copiedField} formatDate={formatRelativeDate} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <ArrowLeft size={20} className="text-white" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center space-x-3">
                <FolderOpen size={28} className="text-blue-400" weight="fill" />
                <span>My Projects</span>
              </h1>
              <p className="text-slate-400 text-sm mt-1">Every saved generation, in one place</p>
            </div>
          </div>
          
          {/* Search and Controls */}
          <div className="flex items-center space-x-3">
            {/* 2026-05-29: Founder-only Import Existing Video.
                Multipart upload → /api/videos/import. NEVER calls
                Runway / ElevenLabs / generates video. */}
            {isInternalAccount && (
              <ImportVideoButton onImported={() => fetchAllData()} />
            )}

            {/* Search */}
            <div className="relative flex-1 sm:flex-none">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search creations..."
                data-testid="search-input"
                className="w-full sm:w-64 pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            
            {/* Sort Toggle */}
            <button
              onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
              data-testid="sort-toggle"
              className="flex items-center space-x-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors text-sm"
            >
              <SortAscending size={16} className={sortOrder === 'oldest' ? 'rotate-180' : ''} />
              <span className="hidden sm:inline">{sortOrder === 'newest' ? 'Newest' : 'Oldest'}</span>
            </button>
            
            {/* Refresh */}
            <button
              onClick={fetchAllData}
              disabled={loading}
              data-testid="refresh-btn"
              className="w-10 h-10 bg-white/5 border border-white/10 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors flex items-center justify-center"
            >
              <ArrowsClockwise size={18} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Tabs - Mobile Scrollable */}
        <div className="flex space-x-2 mb-6 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center space-x-2 px-3 sm:px-4 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap text-sm ${
                  activeTab === tab.id
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white border border-transparent'
                }`}
              >
                <Icon size={18} weight={activeTab === tab.id ? 'fill' : 'regular'} />
                <span>{tab.label}</span>
                {tab.count > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.id ? 'bg-blue-500/30' : 'bg-white/10'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-4"
            >
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white/5 rounded-xl p-6 animate-pulse">
                  <div className="h-6 bg-white/10 rounded w-1/3 mb-4" />
                  <div className="h-4 bg-white/10 rounded w-2/3 mb-2" />
                  <div className="h-4 bg-white/10 rounded w-1/2" />
                </div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {/* All Tab */}
              {activeTab === 'all' && (
                <div className="space-y-4">
                  {filterItems(getAllItems(), ['topic', 'title', 'script', 'offer_name', 'headline', 'campaign_name', 'original_hook', 'content']).length === 0 ? (
                    <EmptyState 
                      icon={FolderOpen} 
                      title="You haven't created anything yet" 
                      description="Start by generating your first content plan. PayGBot will help you create viral posts, offers, sales pages, and more to grow your business."
                      action={() => navigate('/dashboard')}
                      actionLabel="Create My First Plan"
                    />
                  ) : (
                    filterItems(getAllItems(), ['topic', 'title', 'script', 'offer_name', 'headline', 'campaign_name', 'original_hook', 'content']).map(renderAllItem)
                  )}
                </div>
              )}

              {/* Content Tab */}
              {activeTab === 'content' && (
                <div className="space-y-4">
                  {filterItems(content, ['topic', 'title', 'script']).length === 0 ? (
                    <EmptyState 
                      icon={Sparkle} 
                      title="You haven't created anything yet" 
                      description="Start by generating your first content plan. PayGBot will create scroll-stopping hooks and scripts for you."
                      action={() => navigate('/dashboard')}
                      actionLabel="Create My First Plan"
                    />
                  ) : (
                    filterItems(content, ['topic', 'title', 'script']).map((item) => (
                      <ContentCard 
                        key={item.id} 
                        item={item} 
                        expanded={expandedItems[item.id]}
                        onToggle={() => toggleExpanded(item.id)}
                        onCopy={handleCopy}
                        copiedField={copiedField}
                        formatDate={formatRelativeDate}
                        onCreateVideo={handleCreateVideoFromContent}
                        isCreatingVideo={isCreatingVideo}
                        videoCreationJobId={videoCreationJobId}
                        isInternalAccount={isInternalAccount}
                      />
                    ))
                  )}
                </div>
              )}

              {/* Videos Tab */}
              {activeTab === 'videos' && (
                <div className="space-y-4">
                  {filterItems(videos, ['topic', 'title', 'script']).length === 0 ? (
                    <EmptyState 
                      icon={FilmSlate} 
                      title="No scripts yet" 
                      description="Create content first, then use the video prompt to make videos"
                      action={() => navigate('/dashboard')}
                      actionLabel="Create Content"
                    />
                  ) : (
                    filterItems(videos, ['topic', 'title', 'script']).map((item) => (
                      <VideoCard 
                        key={item.id} 
                        item={item} 
                        expanded={expandedItems[item.id]}
                        onToggle={() => toggleExpanded(item.id)}
                        onCopy={handleCopy}
                        copiedField={copiedField}
                        formatDate={formatRelativeDate}
                      />
                    ))
                  )}
                </div>
              )}

              {/* Offers Tab */}
              {activeTab === 'offers' && (
                <div className="space-y-4">
                  {filterItems(offers, ['offer_name', 'offer_tagline']).length === 0 ? (
                    <EmptyState 
                      icon={Package} 
                      title="No offers yet" 
                      description="Build your first digital offer"
                      action={() => navigate('/dashboard')}
                      actionLabel="Build Offer"
                    />
                  ) : (
                    filterItems(offers, ['offer_name', 'offer_tagline']).map((item) => (
                      <OfferCard 
                        key={item.id} 
                        item={item} 
                        expanded={expandedItems[item.id]}
                        onToggle={() => toggleExpanded(item.id)}
                        onCopy={handleCopy}
                        copiedField={copiedField}
                        formatDate={formatRelativeDate}
                      />
                    ))
                  )}
                </div>
              )}

              {/* Sales Pages Tab */}
              {activeTab === 'sales-pages' && (
                <div className="space-y-4">
                  {filterItems(salesPages, ['headline', 'subheadline']).length === 0 ? (
                    <EmptyState 
                      icon={Browser} 
                      title="No sales pages yet" 
                      description="Generate your first sales page"
                      action={() => navigate('/dashboard')}
                      actionLabel="Create Sales Page"
                    />
                  ) : (
                    filterItems(salesPages, ['headline', 'subheadline']).map((item) => (
                      <SalesPageCard 
                        key={item.id} 
                        item={item} 
                        expanded={expandedItems[item.id]}
                        onToggle={() => toggleExpanded(item.id)}
                        onCopy={handleCopy}
                        copiedField={copiedField}
                        formatDate={formatRelativeDate}
                      />
                    ))
                  )}
                </div>
              )}

              {/* Emails Tab */}
              {activeTab === 'emails' && (
                <div className="space-y-4">
                  {filterItems(emailCampaigns, ['campaign_name']).length === 0 ? (
                    <EmptyState 
                      icon={Envelope} 
                      title="No email campaigns yet" 
                      description="Create your first email sequence"
                      action={() => navigate('/dashboard')}
                      actionLabel="Create Campaign"
                    />
                  ) : (
                    filterItems(emailCampaigns, ['campaign_name']).map((item) => (
                      <EmailCampaignCard 
                        key={item.id} 
                        item={item} 
                        expanded={expandedItems[item.id]}
                        onToggle={() => toggleExpanded(item.id)}
                        onCopy={handleCopy}
                        copiedField={copiedField}
                        formatDate={formatRelativeDate}
                      />
                    ))
                  )}
                </div>
              )}

              {/* Hooks Tab */}
              {activeTab === 'hooks' && (
                <div className="space-y-4">
                  {filterItems(hooks, ['original_hook', 'improved_hooks']).length === 0 ? (
                    <EmptyState 
                      icon={PenNib} 
                      title="No improved hooks yet" 
                      description="Use the 'Improve My Hook' tool to create better hooks"
                      action={() => navigate('/dashboard')}
                      actionLabel="Improve Hook"
                    />
                  ) : (
                    filterItems(hooks, ['original_hook', 'improved_hooks']).map((item) => (
                      <HookCard 
                        key={item.id} 
                        item={item} 
                        expanded={expandedItems[item.id]}
                        onToggle={() => toggleExpanded(item.id)}
                        onCopy={handleCopy}
                        copiedField={copiedField}
                        formatDate={formatRelativeDate}
                      />
                    ))
                  )}
                </div>
              )}

              {/* Scored Content Tab */}
              {activeTab === 'scored' && (
                <div className="space-y-4">
                  {filterItems(scoredContent, ['content', 'overall_score']).length === 0 ? (
                    <EmptyState 
                      icon={Star} 
                      title="No scored content yet" 
                      description="Use the 'Score My Content' tool to get feedback"
                      action={() => navigate('/dashboard')}
                      actionLabel="Score Content"
                    />
                  ) : (
                    filterItems(scoredContent, ['content', 'overall_score']).map((item) => (
                      <ScoredContentCard 
                        key={item.id} 
                        item={item} 
                        expanded={expandedItems[item.id]}
                        onToggle={() => toggleExpanded(item.id)}
                        onCopy={handleCopy}
                        copiedField={copiedField}
                        formatDate={formatRelativeDate}
                      />
                    ))
                  )}
                </div>
              )}

              {/* Business Plans Tab */}
              {activeTab === 'business-plans' && (
                <div className="space-y-4">
                  {filterItems(businessPlans, ['title', 'niche']).length === 0 ? (
                    <EmptyState 
                      icon={Target} 
                      title="No business plans yet" 
                      description="Create your first business blueprint with the Business Builder Bot"
                      action={() => navigate('/dashboard')}
                      actionLabel="Build My Plan"
                    />
                  ) : (
                    filterItems(businessPlans, ['title', 'niche']).map((item) => (
                      <BusinessPlanCard 
                        key={item.id} 
                        item={item} 
                        expanded={expandedItems[item.id]}
                        onToggle={() => toggleExpanded(item.id)}
                        onCopy={handleCopy}
                        copiedField={copiedField}
                        formatDate={formatRelativeDate}
                      />
                    ))
                  )}
                </div>
              )}

              {/* Content Packs Tab */}
              {activeTab === 'content-packs' && (
                <div className="space-y-4">
                  {filterItems(contentPacks, ['title', 'niche', 'platform']).length === 0 ? (
                    <EmptyState 
                      icon={Files} 
                      title="No content packs yet" 
                      description="Generate a content pack with multiple ready-to-post pieces"
                      action={() => navigate('/dashboard')}
                      actionLabel="Create Content Pack"
                    />
                  ) : (
                    filterItems(contentPacks, ['title', 'niche', 'platform']).map((item) => (
                      <ContentPackCard 
                        key={item.id} 
                        item={item} 
                        expanded={expandedItems[item.id]}
                        onToggle={() => toggleExpanded(item.id)}
                        onCopy={handleCopy}
                        copiedField={copiedField}
                        formatDate={formatRelativeDate}
                        onCreateBulkVideos={handleCreateBulkVideos}
                        isCreatingBulk={isCreatingBulk}
                        isInternalAccount={isInternalAccount}
                      />
                    ))
                  )}
                </div>
              )}

              {/* Link in Bio Tab */}
              {activeTab === 'link-in-bio' && (
                <div className="space-y-4">
                  {filterItems(linkInBio, ['title', 'niche', 'bio_text']).length === 0 ? (
                    <EmptyState 
                      icon={Link} 
                      title="No link setups yet" 
                      description="Create your optimized link in bio structure"
                      action={() => navigate('/dashboard')}
                      actionLabel="Create Link Setup"
                    />
                  ) : (
                    filterItems(linkInBio, ['title', 'niche', 'bio_text']).map((item) => (
                      <LinkInBioCard 
                        key={item.id} 
                        item={item} 
                        expanded={expandedItems[item.id]}
                        onToggle={() => toggleExpanded(item.id)}
                        onCopy={handleCopy}
                        copiedField={copiedField}
                        formatDate={formatRelativeDate}
                      />
                    ))
                  )}
                </div>
              )}

              {/* Business Packages Tab - Visual Thumbnails */}
              {activeTab === 'business-packages' && (
                <div className="space-y-4">
                  {businessPackages.length === 0 ? (
                    <EmptyState 
                      icon={Package} 
                      title="No business packages yet" 
                      description="Pro users can generate complete business packages with all assets"
                      action={() => navigate('/dashboard')}
                      actionLabel="Go to Dashboard"
                    />
                  ) : (
                    businessPackages.map((item) => (
                      <BusinessPackageCard
                        key={item.id}
                        item={item}
                        expanded={expandedItems[`package-${item.id}`]}
                        onToggle={() => toggleExpanded(`package-${item.id}`)}
                        formatDate={formatRelativeDate}
                        planTier={user?.plan_tier || 'pro'}
                        onPreview={(output) => {
                          // Round 58 — open the complete saved asset
                          // (PDF + HTML + every editable field).
                          const id = output?.id || output?.creation_id || item?.id;
                          if (id) {
                            setReopenId(id);
                          } else {
                            toast.info(`Previewing ${output.filename}...`);
                          }
                        }}
                        onDownload={(output) => {
                          toast.info(`Downloading ${output.filename}...`);
                        }}
                        onCopy={(output) => {
                          handleCopy(output.content || output.filename, `output-${output.filename}`);
                        }}
                        onDelete={(id) => {
                          setBusinessPackages(prev => prev.filter(p => p.id !== id));
                        }}
                      />
                    ))
                  )}
                </div>
              )}

              {/* DFY Videos Tab */}
              {activeTab === 'dfy-videos' && (
                <div className="space-y-4">
                  {filterItems(dfyVideos, ['title', 'script']).length === 0 ? (
                    <EmptyState 
                      icon={VideoCamera} 
                      title="No generated videos yet" 
                      description="Create content and click 'Create this video for me' to generate videos"
                      action={() => navigate('/dashboard')}
                      actionLabel="Create Content"
                    />
                  ) : (
                    filterItems(dfyVideos, ['title', 'script']).map((item) => (
                      <DfyVideoCard 
                        key={item.id} 
                        item={item} 
                        expanded={expandedItems[item.id]}
                        onToggle={() => toggleExpanded(item.id)}
                        onCopy={handleCopy}
                        copiedField={copiedField}
                        formatDate={formatRelativeDate}
                        isInternalAccount={isInternalAccount}
                        v2EnvAvailable={v2EnvAvailable}
                        onRetryV2={handleRetryWithV2}
                        retryV2JobId={retryV2JobId}
                        retryV2SourceId={retryV2SourceId}
                        onDelete={handleDeleteCreation}
                      />
                    ))
                  )}
                </div>
              )}

              {/* Ebooks Tab */}
              {activeTab === 'ebooks' && (
                <div className="space-y-4">
                  {filterItems(ebooks, ['title']).length === 0 ? (
                    <EmptyState
                      icon={Files}
                      title="No ebooks yet"
                      description="Run the Ebook builder — your saved ebooks will appear here automatically."
                      action={() => navigate('/dashboard')}
                      actionLabel="Create an Ebook"
                    />
                  ) : (
                    filterItems(ebooks, ['title']).map((item) => (
                      <EbookCard
                        key={item.id}
                        item={item}
                        expanded={expandedItems[item.id]}
                        onToggle={() => toggleExpanded(item.id)}
                        onCopy={handleCopy}
                        copiedField={copiedField}
                        formatDate={formatRelativeDate}
                      />
                    ))
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Bulk Video Generation Modal */}
      <BulkVideoModal
        isOpen={showBulkVideoModal}
        onClose={() => {
          setShowBulkVideoModal(false);
          setBulkVideoItems([]);
          setBulkVideoPackTitle('');
          fetchAllData();
        }}
        contentItems={bulkVideoItems}
        sourceType="content_pack"
        packTitle={bulkVideoPackTitle}
        videoCredits={videoCredits}
        onRefreshCredits={fetchVideoCredits}
      />
      
      {/* Floating Help Button */}
      <FloatingHelpButton />

      {/* Round 58/59 — Reopen full saved asset, with delete & tier-aware exports. */}
      <CreationDetailModal
        creationId={reopenId}
        onClose={() => setReopenId(null)}
        onDeleted={() => {
          // The modal already issued the DELETE; just refresh the list
          // so the row disappears from My Projects immediately.
          fetchAllData();
        }}
      />
    </div>
  );
};

// Type Badge Component
const TypeBadge = ({ type, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-500/20 text-blue-400',
    purple: 'bg-purple-500/20 text-purple-400',
    amber: 'bg-amber-500/20 text-amber-400',
    green: 'bg-green-500/20 text-green-400',
    pink: 'bg-pink-500/20 text-pink-400',
    cyan: 'bg-cyan-500/20 text-cyan-400'
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[color]}`}>
      {type}
    </span>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  // Round 218A Phase 8D-2A (2026-02-05) — canonical customer-facing
  // lifecycle labels. The customer sees the workflow state, not the
  // provider mechanics. Failed variants now clearly communicate that
  // credits were refunded so the customer immediately understands
  // their work AND their credits are safe.
  const statusConfig = {
    'completed': { color: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30', label: 'Generated' },
    'queued': { color: 'bg-slate-500/20 text-slate-300', label: 'Queued' },
    'pending': { color: 'bg-slate-500/20 text-slate-300', label: 'Queued' },
    'processing': { color: 'bg-amber-500/20 text-amber-300', label: 'Generating' },
    'voiceover_processing': { color: 'bg-amber-500/20 text-amber-300', label: 'Generating' },
    'video_processing': { color: 'bg-amber-500/20 text-amber-300', label: 'Generating' },
    'composing': { color: 'bg-amber-500/20 text-amber-300', label: 'Generating' },
    'validating': { color: 'bg-amber-500/20 text-amber-300', label: 'Generating' },
    'failed': { color: 'bg-rose-500/20 text-rose-300 border border-rose-500/40', label: 'Failed — Credits Refunded' },
    'failed_no_url': { color: 'bg-rose-500/20 text-rose-300 border border-rose-500/40', label: 'Failed — Credits Refunded' },
    'failed_voiceover': { color: 'bg-rose-500/20 text-rose-300 border border-rose-500/40', label: 'Failed — Credits Refunded' },
    'failed_runway': { color: 'bg-rose-500/20 text-rose-300 border border-rose-500/40', label: 'Failed — Credits Refunded' },
    'failed_composition': { color: 'bg-rose-500/20 text-rose-300 border border-rose-500/40', label: 'Failed — Credits Refunded' },
    'failed_validation': { color: 'bg-rose-500/20 text-rose-300 border border-rose-500/40', label: 'Failed — Credits Refunded' },
    'needs_repair': { color: 'bg-amber-500/20 text-amber-300 border border-amber-500/40', label: 'Needs Repair' },
    'blocked_max_retries': { color: 'bg-rose-500/20 text-rose-300 border border-rose-500/40', label: 'Failed — Credits Refunded' },
    'blocked_no_credits': { color: 'bg-rose-500/20 text-rose-300 border border-rose-500/40', label: 'Production Ready' },
    'blocked_no_script': { color: 'bg-slate-500/20 text-slate-300', label: 'Draft' },
    'prompt_ready': { color: 'bg-blue-500/20 text-blue-300 border border-blue-500/30', label: 'Production Ready' },
    'content_ready': { color: 'bg-blue-500/20 text-blue-300 border border-blue-500/30', label: 'Production Ready' },
    // Round 218A Phase 2 P0 — canonical media_status values from
    // backend `services/media_integrity.py::MediaStatus`. Every
    // customer-visible tile now renders one of these instead of
    // ever showing a broken player or thumbnail.
    'ready':               { color: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30', label: 'Generated' },
    'needs_approval':      { color: 'bg-amber-500/20 text-amber-300 border border-amber-500/40', label: 'Production Ready' },
    'needs_regeneration':  { color: 'bg-amber-500/20 text-amber-300 border border-amber-500/40', label: 'Needs Regeneration' },
    'unavailable':         { color: 'bg-slate-500/20 text-slate-400 border border-slate-500/30', label: 'Archived' },
    'draft':               { color: 'bg-slate-600/40 text-slate-200 border border-slate-500/30', label: 'Draft' },
    'production_ready':    { color: 'bg-blue-500/20 text-blue-300 border border-blue-500/30', label: 'Production Ready' },
    'generating':          { color: 'bg-amber-500/20 text-amber-300 border border-amber-500/40', label: 'Generating' },
    'generated':           { color: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30', label: 'Generated' },
    'archived':            { color: 'bg-slate-500/20 text-slate-400 border border-slate-500/30', label: 'Archived' },
  };
  const config = statusConfig[status] || statusConfig['content_ready'];
  return <span className={`px-2 py-1 rounded text-xs ${config.color}`} data-testid={`status-badge-${status}`}>{config.label}</span>;
};

// Round 218A Phase 2 P0 — a small helper to decide whether a video/
// image tile should even attempt to render the media element. When
// media_status is anything OTHER than 'ready' / 'completed', the
// caller must swap the <video>/<img> for a status placeholder tile.
// Keeps the "broken player" from ever appearing on My Projects.
const isMediaPlayable = (item) => {
  const s = (item && item.media_status) || '';
  return s === 'ready' || s === '' || s === 'completed';
};

// Reusable placeholder that fills the same footprint as a video
// preview when the media is unavailable / needs regeneration.
const MediaStatusTile = ({ item }) => {
  const status = (item && item.media_status) || 'unavailable';
  const isRegen = status === 'needs_regeneration';
  const msg = isRegen
    ? 'This media needs to be regenerated. Your project is preserved — regenerate the asset to view it again.'
    : status === 'processing'
      ? 'Your media is still generating. Check back in a moment.'
      : 'Preview unavailable. The source file is no longer accessible.';
  return (
    <div
      className="w-full aspect-video flex items-center justify-center bg-slate-900/60 border border-white/10 rounded-lg"
      data-testid={`media-status-tile-${status}`}
    >
      <div className="text-center px-4 py-6 max-w-sm">
        <div className="inline-block mb-3">
          <StatusBadge status={status} />
        </div>
        <p className="text-slate-300 text-sm">{msg}</p>
      </div>
    </div>
  );
};

// Empty state component
const EmptyState = ({ icon: Icon, title, description, action, actionLabel }) => (
  <div className="text-center py-16 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-2xl border border-blue-500/20">
    <Icon size={56} className="text-blue-400/60 mx-auto mb-4" />
    <h3 className="text-white font-semibold text-xl mb-3">{title}</h3>
    <p className="text-slate-400 mb-6 max-w-md mx-auto leading-relaxed">{description}</p>
    {action && (
      <button
        onClick={action}
        data-testid="empty-state-cta"
        className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-xl transition-all flex items-center space-x-2 mx-auto"
      >
        <Sparkle size={18} weight="fill" />
        <span>{actionLabel}</span>
        <ArrowRight size={18} />
      </button>
    )}
  </div>
);

// Content card component
const ContentCard = ({ item, expanded, onToggle, onCopy, copiedField, formatDate, onCreateVideo, isCreatingVideo, videoCreationJobId, isInternalAccount = false }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid={`content-card-${item.id}`}>
    <div
      onClick={onToggle}
      className="w-full p-4 sm:p-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors cursor-pointer"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-3 mb-2 flex-wrap gap-2">
          <Sparkle size={18} className="text-purple-400 flex-shrink-0" weight="fill" />
          <h3 className="text-white font-semibold truncate">{item.title}</h3>
          <TypeBadge type="Content" color="purple" />
          {/* Show video ready badge if video_prompt exists */}
          {item.video_prompt && <TypeBadge type="Video Ready" color="fuchsia" />}
        </div>
        <p className="text-slate-400 text-sm truncate">{item.topic}</p>
        <p className="text-slate-500 text-xs mt-1">{formatDate(item.created_at)}</p>
      </div>
      <div className="flex items-center gap-2 ml-2 sm:ml-4">
        <DeliverableActions
          creationId={item.id}
          title={item.title || 'Content'}
          type="content_pack"
          copyText={[item.title, item.topic, item.hook, item.caption, (Array.isArray(item.hashtags) ? item.hashtags : []).join(' ')].filter(Boolean).join('\n\n')}
          onView={(id) => window.dispatchEvent(new CustomEvent('paygbot:open-creation', { detail: id }))}
          onDeleted={() => window.dispatchEvent(new CustomEvent('paygbot:refresh-creations'))}
        />
        <StatusBadge status={item.video_prompt ? 'prompt_ready' : 'content_ready'} />
        {expanded
          ? <CaretUp size={20} className="text-slate-400 hidden sm:block" />
          : <CaretDown size={20} className="text-slate-400 hidden sm:block" />}
      </div>
    </div>
    
    {expanded && (
      <div className="px-4 sm:px-5 pb-5 space-y-4 border-t border-white/10 pt-4">
        {/* PART 10: Create Video Action - Prominent placement at top */}
        {/* Phase B (2026-05-27) — Hide the legacy "Create Video" CTA
            from clients. Founder still sees it (will route to v2 in
            Phase C). Clients see only the video prompt + Copy action
            below. */}
        {item.video_prompt && isInternalAccount && (
          <div className="bg-gradient-to-r from-fuchsia-500/10 to-violet-500/10 border border-fuchsia-500/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <FilmSlate size={18} className="text-fuchsia-400" weight="fill" />
                <h4 className="text-white font-medium">Create Video</h4>
              </div>
              {isCreatingVideo && videoCreationJobId === item.id && (
                <span className="text-xs text-fuchsia-400">Generating...</span>
              )}
            </div>
            <p className="text-slate-400 text-sm mb-3">
              Turn this content into a ready-to-post video with voiceover and captions.
            </p>
            <button
              onClick={() => onCreateVideo(item)}
              disabled={isCreatingVideo && videoCreationJobId === item.id}
              data-testid={`create-video-btn-${item.id}`}
              className={`w-full py-2.5 text-sm font-semibold rounded-lg transition-all flex items-center justify-center space-x-2 ${
                isCreatingVideo && videoCreationJobId === item.id
                  ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-fuchsia-600 to-violet-600 hover:from-fuchsia-700 hover:to-violet-700 text-white'
              }`}
            >
              {isCreatingVideo && videoCreationJobId === item.id ? (
                <>
                  <Spinner size={16} className="animate-spin" />
                  <span>Creating Video...</span>
                </>
              ) : (
                <>
                  <FilmSlate size={16} weight="fill" />
                  <span>Create Video</span>
                </>
              )}
            </button>
          </div>
        )}
        
        {/* PART 9: Content sections with Copy buttons */}
        <ContentSection label="Hook" content={item.selected_hook} onCopy={onCopy} copiedField={copiedField} id={`${item.id}-hook`} highlight />
        <ContentSection label="Script" content={item.script} onCopy={onCopy} copiedField={copiedField} id={`${item.id}-script`} />
        <ContentSection label="Caption" content={item.caption} onCopy={onCopy} copiedField={copiedField} id={`${item.id}-caption`} />
        
        {/* Video Prompt with Copy */}
        {item.video_prompt && (
          <ContentSection 
            label="Video Prompt" 
            content={item.video_prompt} 
            onCopy={onCopy} 
            copiedField={copiedField} 
            id={`${item.id}-video-prompt`} 
            accentColor="fuchsia"
          />
        )}
        
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Hashtags</label>
            {item.hashtags?.length > 0 && (
              <button
                onClick={() => onCopy(item.hashtags?.map(t => `#${t}`).join(' '), `${item.id}-hashtags`)}
                className={`text-xs px-2 py-1 rounded transition-colors flex items-center space-x-1 ${
                  copiedField === `${item.id}-hashtags` 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-white/10 text-slate-400 hover:text-white'
                }`}
              >
                {copiedField === `${item.id}-hashtags` ? <CheckCircle size={12} /> : <Copy size={12} />}
                <span>{copiedField === `${item.id}-hashtags` ? 'Copied!' : 'Copy All'}</span>
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {item.hashtags?.map((tag, i) => (
              <span key={i} className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm">#{tag}</span>
            ))}
          </div>
        </div>
      </div>
    )}
  </div>
);

// Video card component - Now shows video prompt for external creation
const VideoCard = ({ item, expanded, onToggle, onCopy, copiedField, formatDate }) => {
  const hasPrompt = item.video_prompt;
  
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid={`video-card-${item.id}`}>
      <div
        onClick={onToggle}
        className="w-full p-4 sm:p-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors cursor-pointer"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-3 mb-2 flex-wrap gap-2">
            <VideoCamera size={18} className="text-violet-400 flex-shrink-0" weight="fill" />
            <h3 className="text-white font-semibold truncate">{item.title}</h3>
            <TypeBadge type="Content" color="purple" />
          </div>
          <p className="text-slate-400 text-sm truncate">{item.topic}</p>
          <p className="text-slate-500 text-xs mt-1">{formatDate(item.created_at)}</p>
        </div>
        <div className="flex items-center gap-2 ml-2 sm:ml-4">
          <DeliverableActions
            creationId={item.id}
            title={item.title || 'Video content'}
            type="content_pack"
            copyText={[item.title, item.topic, item.video_prompt, item.hook, item.caption].filter(Boolean).join('\n\n')}
            onView={(id) => window.dispatchEvent(new CustomEvent('paygbot:open-creation', { detail: id }))}
            onDeleted={() => window.dispatchEvent(new CustomEvent('paygbot:refresh-creations'))}
          />
          <StatusBadge status={hasPrompt ? 'prompt_ready' : 'content_ready'} />
          {expanded
            ? <CaretUp size={20} className="text-slate-400 hidden sm:block" />
            : <CaretDown size={20} className="text-slate-400 hidden sm:block" />}
        </div>
      </div>
      
      {expanded && (
        <div className="px-4 sm:px-5 pb-5 space-y-4 border-t border-white/10 pt-4">
          {/* Create Your Video Section */}
          {hasPrompt && (
            <div className="bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 border border-violet-500/30 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <VideoCamera size={18} className="text-violet-400" weight="fill" />
                <h4 className="text-white font-medium">Create Your Video</h4>
              </div>
              <p className="text-slate-400 text-sm mb-3">
                Copy this prompt and paste into SeaDance, Runway, Pika, or any AI video tool
              </p>
              <div className="p-3 bg-black/30 rounded-lg mb-3">
                <p className="text-slate-200 text-sm leading-relaxed">{item.video_prompt}</p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onCopy(item.video_prompt, `${item.id}-video-prompt`)}
                  data-testid="copy-video-prompt"
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 font-medium rounded-lg transition-all ${
                    copiedField === `${item.id}-video-prompt`
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white'
                  }`}
                >
                  {copiedField === `${item.id}-video-prompt` ? (
                    <>
                      <CheckCircle size={18} />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy size={18} />
                      <span>Copy Video Prompt</span>
                    </>
                  )}
                </button>
                {item.video_prompt_alt && (
                  <button
                    onClick={() => onCopy(item.video_prompt_alt, `${item.id}-video-prompt-alt`)}
                    className={`px-4 py-2.5 font-medium rounded-lg transition-all ${
                      copiedField === `${item.id}-video-prompt-alt`
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                        : 'bg-white/10 hover:bg-white/20 text-slate-300 border border-white/10'
                    }`}
                    title="Copy alternative prompt"
                  >
                    Alt
                  </button>
                )}
              </div>
            </div>
          )}
          
          <ContentSection label="Script" content={item.script} onCopy={onCopy} copiedField={copiedField} id={`${item.id}-script`} />
          <ContentSection label="Caption" content={item.caption} onCopy={onCopy} copiedField={copiedField} id={`${item.id}-caption`} />
        </div>
      )}
    </div>
  );
};

// Offer card component
const OfferCard = ({ item, expanded, onToggle, onCopy, copiedField, formatDate }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid={`offer-card-${item.id}`}>
    <div
      onClick={onToggle}
      className="w-full p-4 sm:p-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors cursor-pointer"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-3 mb-2 flex-wrap gap-2">
          <Package size={18} className="text-amber-400 flex-shrink-0" weight="fill" />
          <h3 className="text-white font-semibold truncate">{item.offer_name}</h3>
          <TypeBadge type="Offer" color="amber" />
          <TypeBadge type={item.offer_type?.replace('_', ' ')} color="amber" />
        </div>
        <p className="text-amber-400/80 text-sm truncate">{item.offer_tagline}</p>
        <p className="text-slate-500 text-xs mt-1">{formatDate(item.created_at)}</p>
      </div>
      <div className="flex items-center gap-2 ml-2 sm:ml-4">
        {/* Round 62 — uniform card-level actions. */}
        <DeliverableActions
          creationId={item.id}
          title={item.offer_name || 'Offer'}
          type="offer"
          copyText={[item.offer_name, item.offer_tagline, item.offer_pitch].filter(Boolean).join('\n\n')}
          onView={(id) => window.dispatchEvent(new CustomEvent('paygbot:open-creation', { detail: id }))}
          onDeleted={() => window.dispatchEvent(new CustomEvent('paygbot:refresh-creations'))}
        />
        {expanded
          ? <CaretUp size={20} className="text-slate-400 hidden sm:block" />
          : <CaretDown size={20} className="text-slate-400 hidden sm:block" />}
      </div>
    </div>
    
    {expanded && (
      <div className="px-4 sm:px-5 pb-5 space-y-4 border-t border-white/10 pt-4">
        <ContentSection label="Tagline" content={item.offer_tagline} onCopy={onCopy} copiedField={copiedField} id={`${item.id}-tagline`} highlight accentColor="amber" />
        <ContentSection label="Description" content={item.offer_description} onCopy={onCopy} copiedField={copiedField} id={`${item.id}-desc`} />
        <div>
          <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Key Benefits</label>
          <ul className="space-y-2">
            {item.key_benefits?.map((benefit, i) => (
              <li key={i} className="flex items-start space-x-2 text-slate-300 bg-white/5 p-3 rounded-lg">
                <CheckCircle size={16} className="text-green-400 mt-0.5 flex-shrink-0" weight="fill" />
                <span className="text-sm">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
        {item.price_suggestion && (
          <ContentSection label="Price Suggestion" content={item.price_suggestion} highlight accentColor="green" />
        )}
      </div>
    )}
  </div>
);

// Sales page card component
// Round 60 — shared field normaliser used by SalesPageCard and the
// CreationDetailModal sales-page renderer. The SalesPageBuilderService
// persists benefits/bonuses/faq sections as List[Dict] (e.g.
// [{benefit, description}, …]); passing those directly to a React text
// child throws "Objects are not valid as a React child" and crashes
// /my-creations. This helper coerces any field (string, list of
// strings, list of {keyA,keyB} objects) to a single readable string
// — Copy buttons keep working because the resulting string is what
// gets copied.
function renderSalesPageField(value, dictKeys = ['title', 'name', 'text', 'description']) {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value
      .map((it) => {
        if (it === null || it === undefined) return '';
        if (typeof it === 'string' || typeof it === 'number') return `• ${it}`;
        if (typeof it === 'object') {
          // Pull the two most-meaningful keys for sales-page lists.
          const primary = dictKeys.map((k) => it[k]).find((v) => typeof v === 'string' && v) || '';
          // The 2nd configured key, if different from the one we picked.
          const usedKey = dictKeys.find((k) => it[k] === primary);
          const secondary = dictKeys
            .filter((k) => k !== usedKey)
            .map((k) => it[k])
            .find((v) => typeof v === 'string' && v) || '';
          if (primary && secondary) return `• ${primary} — ${secondary}`;
          if (primary) return `• ${primary}`;
          // Unknown shape — last resort, pick any string value.
          const anyStr = Object.values(it).find((v) => typeof v === 'string' && v) || '';
          return anyStr ? `• ${anyStr}` : '';
        }
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }
  if (typeof value === 'object') {
    const k = dictKeys.find((x) => typeof value[x] === 'string' && value[x]);
    return k ? value[k] : '';
  }
  return String(value);
}

const SalesPageCard = ({ item, expanded, onToggle, onCopy, copiedField, formatDate }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid={`salespage-card-${item.id}`}>
    <div
      onClick={onToggle}
      className="w-full p-4 sm:p-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors cursor-pointer"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-3 mb-2 flex-wrap gap-2">
          <Browser size={18} className="text-purple-400 flex-shrink-0" weight="fill" />
          <h3 className="text-white font-semibold truncate">{item.headline}</h3>
          <TypeBadge type="Sales Page" color="purple" />
        </div>
        <p className="text-purple-400/80 text-sm truncate">{item.subheadline}</p>
        <p className="text-slate-500 text-xs mt-1">{formatDate(item.created_at)}</p>
      </div>
      <div className="flex items-center gap-2 ml-2 sm:ml-4">
        {/* Round 61 — uniform card-level actions. Replaces legacy
            "Copy HTML" chip + "Open Full · Download PDF / HTML"
            combined button. Mobile-friendly hit targets. */}
        <DeliverableActions
          creationId={item.id}
          title={item.headline || item.title || 'Sales page'}
          type="sales_page"
          copyText={item.full_page_html || ''}
          onView={(id) => window.dispatchEvent(new CustomEvent('paygbot:open-creation', { detail: id }))}
          onDeleted={() => window.dispatchEvent(new CustomEvent('paygbot:refresh-creations'))}
        />
        {expanded
          ? <CaretUp size={20} className="text-slate-400 hidden sm:block" />
          : <CaretDown size={20} className="text-slate-400 hidden sm:block" />}
      </div>
    </div>
    
    {expanded && (
      <div className="px-4 sm:px-5 pb-5 space-y-4 border-t border-white/10 pt-4" data-testid={`salespage-card-expanded-${item.id}`}>
        {/* Round 60 (final) — render the FULL saved sales-page schema.
            Some fields are List[Dict] (benefits_section/bonuses_section/
            faq_suggestions) — those MUST be normalised to a renderable
            string before passing to <ContentSection/>, otherwise React
            throws 'Objects are not valid as a React child' and crashes
            the entire page. */}
        <ContentSection label="Headline" content={item.headline} onCopy={onCopy} copiedField={copiedField} id={`${item.id}-headline`} highlight accentColor="purple" />
        {item.subheadline && (
          <ContentSection label="Subheadline" content={item.subheadline} onCopy={onCopy} copiedField={copiedField} id={`${item.id}-sub`} />
        )}
        {item.problem_section && (
          <ContentSection label="Problem" content={renderSalesPageField(item.problem_section)} onCopy={onCopy} copiedField={copiedField} id={`${item.id}-problem`} />
        )}
        {item.solution_section && (
          <ContentSection label="Solution" content={renderSalesPageField(item.solution_section)} onCopy={onCopy} copiedField={copiedField} id={`${item.id}-solution`} />
        )}
        {item.benefits_section && (
          <ContentSection label="Benefits" content={renderSalesPageField(item.benefits_section, ['benefit', 'description'])} onCopy={onCopy} copiedField={copiedField} id={`${item.id}-benefits`} />
        )}
        {item.offer_section && (
          <ContentSection label="Offer" content={renderSalesPageField(item.offer_section)} onCopy={onCopy} copiedField={copiedField} id={`${item.id}-offer`} />
        )}
        {item.bonuses_section && (
          <ContentSection label="Bonuses" content={renderSalesPageField(item.bonuses_section, ['bonus', 'description'])} onCopy={onCopy} copiedField={copiedField} id={`${item.id}-bonuses`} />
        )}
        {item.cta_section && (
          <ContentSection label="Call to Action" content={renderSalesPageField(item.cta_section)} onCopy={onCopy} copiedField={copiedField} id={`${item.id}-cta`} highlight accentColor="green" />
        )}
        {item.faq_suggestions && (
          <ContentSection label="FAQ" content={renderSalesPageField(item.faq_suggestions, ['question', 'answer'])} onCopy={onCopy} copiedField={copiedField} id={`${item.id}-faq`} />
        )}
      </div>
    )}
  </div>
);

// Email campaign card component
const EmailCampaignCard = ({ item, expanded, onToggle, onCopy, copiedField, formatDate }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid={`email-card-${item.id}`}>
    <div
      onClick={onToggle}
      className="w-full p-4 sm:p-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors cursor-pointer"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-3 mb-2 flex-wrap gap-2">
          <Envelope size={18} className="text-blue-400 flex-shrink-0" weight="fill" />
          <h3 className="text-white font-semibold truncate">{item.campaign_name}</h3>
          <TypeBadge type="Email" color="blue" />
          <TypeBadge type={`${item.emails?.length || 0} emails`} color="cyan" />
        </div>
        <p className="text-blue-400/80 text-sm">{item.campaign_type} campaign</p>
        <p className="text-slate-500 text-xs mt-1">{formatDate(item.created_at)}</p>
      </div>
      <div className="flex items-center gap-2 ml-2 sm:ml-4">
        <DeliverableActions
          creationId={item.id}
          title={item.campaign_name || 'Email campaign'}
          type="email_sequence"
          copyText={(item.emails || []).map((e, i) => `Email ${i+1}: ${e.subject || ''}\n${e.body || e.content || ''}`).join('\n\n---\n\n')}
          onView={(id) => window.dispatchEvent(new CustomEvent('paygbot:open-creation', { detail: id }))}
          onDeleted={() => window.dispatchEvent(new CustomEvent('paygbot:refresh-creations'))}
        />
        {expanded
          ? <CaretUp size={20} className="text-slate-400 hidden sm:block" />
          : <CaretDown size={20} className="text-slate-400 hidden sm:block" />}
      </div>
    </div>
    
    {expanded && (
      <div className="px-4 sm:px-5 pb-5 space-y-3 border-t border-white/10 pt-4">
        {item.emails?.map((email, i) => (
          <div key={i} className="bg-white/5 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-blue-400 text-xs font-medium">Email {i + 1} • {item.send_schedule?.[i]}</span>
              <button
                onClick={() => onCopy(`Subject: ${email.subject}\n\n${email.body}\n\nCTA: ${email.cta}`, `${item.id}-email-${i}`)}
                className="text-slate-500 hover:text-white text-xs flex items-center space-x-1"
              >
                {copiedField === `${item.id}-email-${i}` ? <CheckCircle size={12} /> : <Copy size={12} />}
                <span>Copy</span>
              </button>
            </div>
            <p className="text-white font-medium text-sm mb-1">{email.subject}</p>
            <p className="text-slate-400 text-sm line-clamp-2">{email.body}</p>
          </div>
        ))}
      </div>
    )}
  </div>
);

// Hook card component
const HookCard = ({ item, expanded, onToggle, onCopy, copiedField, formatDate }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid={`hook-card-${item.id}`}>
    <button
      onClick={onToggle}
      className="w-full p-4 sm:p-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-3 mb-2 flex-wrap gap-2">
          <PenNib size={18} className="text-cyan-400 flex-shrink-0" weight="fill" />
          <h3 className="text-white font-semibold truncate">Hook Improvement</h3>
          <TypeBadge type="Hook" color="cyan" />
        </div>
        <p className="text-slate-400 text-sm truncate">{item.original_hook?.slice(0, 80)}...</p>
        <p className="text-slate-500 text-xs mt-1">{formatDate(item.created_at)}</p>
      </div>
      <div className="flex items-center space-x-3 ml-4">
        {expanded ? <CaretUp size={20} className="text-slate-400" /> : <CaretDown size={20} className="text-slate-400" />}
      </div>
    </button>
    
    {expanded && (
      <div className="px-4 sm:px-5 pb-5 space-y-4 border-t border-white/10 pt-4">
        <ContentSection label="Original Hook" content={item.original_hook} onCopy={onCopy} copiedField={copiedField} id={`${item.id}-original`} />
        <div>
          <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Improved Hooks</label>
          <ul className="space-y-2">
            {item.improved_hooks?.map((hook, i) => (
              <li key={i} className="flex items-start justify-between space-x-2 text-slate-300 bg-cyan-500/10 p-3 rounded-lg">
                <span className="text-sm flex-1">{hook}</span>
                <button
                  onClick={() => onCopy(hook, `${item.id}-hook-${i}`)}
                  className="text-cyan-400 hover:text-cyan-300 flex-shrink-0"
                >
                  {copiedField === `${item.id}-hook-${i}` ? <CheckCircle size={16} /> : <Copy size={16} />}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    )}
  </div>
);

// Scored content card component
const ScoredContentCard = ({ item, expanded, onToggle, onCopy, copiedField, formatDate }) => {
  const score = item.overall_score || 0;
  const scoreColor = score >= 8 ? 'text-green-400' : score >= 6 ? 'text-yellow-400' : 'text-red-400';
  
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid={`scored-card-${item.id}`}>
      <button
        onClick={onToggle}
        className="w-full p-4 sm:p-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-3 mb-2 flex-wrap gap-2">
            <Star size={18} className="text-yellow-400 flex-shrink-0" weight="fill" />
            <h3 className="text-white font-semibold truncate">Content Score</h3>
            <TypeBadge type="Scored" color="amber" />
          </div>
          <p className="text-slate-400 text-sm truncate">{item.content?.slice(0, 80)}...</p>
          <p className="text-slate-500 text-xs mt-1">{formatDate(item.created_at)}</p>
        </div>
        <div className="flex items-center space-x-3 ml-4">
          <span className={`text-2xl font-bold ${scoreColor}`}>{score}/10</span>
          {expanded ? <CaretUp size={20} className="text-slate-400" /> : <CaretDown size={20} className="text-slate-400" />}
        </div>
      </button>
      
      {expanded && (
        <div className="px-4 sm:px-5 pb-5 space-y-4 border-t border-white/10 pt-4">
          <ContentSection label="Content" content={item.content} onCopy={onCopy} copiedField={copiedField} id={`${item.id}-content`} />
          
          {/* Score breakdown */}
          {item.scores && (
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Score Breakdown</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {item.scores.map((s, i) => (
                  <div key={i} className="bg-white/5 rounded-lg p-3 text-center">
                    <div className={`text-lg font-bold ${s.score >= 8 ? 'text-green-400' : s.score >= 6 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {s.score}/10
                    </div>
                    <div className="text-xs text-slate-400 capitalize">{s.category}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {item.feedback && (
            <ContentSection label="Feedback" content={item.feedback} />
          )}
          
          {item.suggestions?.length > 0 && (
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Suggestions</label>
              <ul className="space-y-2">
                {item.suggestions.map((suggestion, i) => (
                  <li key={i} className="flex items-start space-x-2 text-slate-300 bg-white/5 p-3 rounded-lg text-sm">
                    <Lightning size={14} className="text-yellow-400 mt-0.5 flex-shrink-0" weight="fill" />
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Reusable content section
const ContentSection = ({ label, content, onCopy, copiedField, id, highlight = false, accentColor = 'blue' }) => {
  const colorClasses = {
    blue: 'text-blue-400 bg-blue-500/10',
    amber: 'text-amber-400 bg-amber-500/10',
    purple: 'text-purple-400 bg-purple-500/10',
    green: 'text-green-400 bg-green-500/10',
    cyan: 'text-cyan-400 bg-cyan-500/10'
  };

  if (!content) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{label}</label>
        {onCopy && id && (
          <button
            onClick={() => onCopy(content, id)}
            className="text-slate-500 hover:text-white text-xs flex items-center space-x-1"
          >
            {copiedField === id ? <CheckCircle size={12} /> : <Copy size={12} />}
            <span>{copiedField === id ? 'Copied!' : 'Copy'}</span>
          </button>
        )}
      </div>
      <p className={`text-sm ${highlight ? `${colorClasses[accentColor]} p-3 rounded-lg font-medium` : 'text-slate-300 bg-white/5 p-3 rounded-lg'}`}>
        {content}
      </p>
    </div>
  );
};

// Content Pack card component
const ContentPackCard = ({ item, expanded, onToggle, onCopy, copiedField, formatDate, onCreateBulkVideos, isCreatingBulk, isInternalAccount = false }) => {
  const posts = item.content?.posts || item.posts || [];
  
  // Extract content items with required video fields
  // Generate video_prompt from hook if not available
  const videoReadyPosts = posts.filter(post => post.script).map((post, idx) => ({
    id: `${item.id}-post-${idx}`,
    title: post.hook || post.topic || `Post ${idx + 1}`,
    script: post.script,
    video_prompt: post.video_prompt || `Professional engaging short video about: ${post.hook || post.topic || 'content'}. Modern, clean visuals with dynamic motion.`,
    selected_hook: post.hook,
    cta: post.cta || 'Follow for more'
  }));
  
  const hasVideoReadyPosts = videoReadyPosts.length > 0;
  
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid={`content-pack-card-${item.id}`}>
      <div
        onClick={onToggle}
        className="w-full p-4 sm:p-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors cursor-pointer"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-3 mb-2 flex-wrap gap-2">
            <Files size={18} className="text-purple-400 flex-shrink-0" weight="fill" />
            <h3 className="text-white font-semibold truncate">{item.title || `Content Pack - ${item.content?.niche || item.niche || 'Unknown'}`}</h3>
            <TypeBadge type="Content Pack" color="purple" />
            <TypeBadge type={`${posts.length} posts`} color="cyan" />
          </div>
          <p className="text-slate-400 text-sm truncate">Platform: {item.content?.platform || item.platform || 'TikTok'}</p>
          <p className="text-slate-500 text-xs mt-1">{formatDate(item.created_at)}</p>
        </div>
        <div className="flex items-center gap-2 ml-2 sm:ml-4">
          <DeliverableActions
            creationId={item.id}
            title={item.title || 'Content Pack'}
            type="content_pack"
            copyText={posts.map((p, i) => `Post ${i+1}: ${p.title || ''}\n${p.hook || ''}\n${p.caption || ''}\n${(Array.isArray(p.hashtags) ? p.hashtags : (typeof p.hashtags === 'string' ? [p.hashtags] : [])).join(' ')}`).join('\n\n---\n\n')}
            onView={(id) => window.dispatchEvent(new CustomEvent('paygbot:open-creation', { detail: id }))}
            onDeleted={() => window.dispatchEvent(new CustomEvent('paygbot:refresh-creations'))}
          />
          {expanded
            ? <CaretUp size={20} className="text-slate-400 hidden sm:block" />
            : <CaretDown size={20} className="text-slate-400 hidden sm:block" />}
        </div>
      </div>
      
      {expanded && (
        <div className="px-4 sm:px-5 pb-5 space-y-3 border-t border-white/10 pt-4">
          {/* PART 1: Create Videos for This Pack button */}
          {/* Phase B (2026-05-27) — Hide bulk legacy video CTA from
              clients. Founder still sees it (Phase D will route the
              bulk path through v2). */}
          {hasVideoReadyPosts && onCreateBulkVideos && isInternalAccount && (
            <button
              onClick={() => onCreateBulkVideos(videoReadyPosts, item.title || 'Content Pack')}
              disabled={isCreatingBulk}
              className="w-full py-3 px-4 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 mb-4"
              data-testid={`bulk-video-btn-${item.id}`}
            >
              {isCreatingBulk ? (
                <>
                  <Spinner size={18} className="animate-spin" />
                  Creating Videos...
                </>
              ) : (
                <>
                  <VideoCamera size={18} weight="fill" />
                  Create {videoReadyPosts.length} Videos
                </>
              )}
            </button>
          )}
          
          {posts.map((post, i) => (
            <div key={i} className="bg-white/5 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-purple-400 text-xs font-medium">Post {i + 1}</span>
                <button
                  onClick={() => onCopy(`Hook: ${post.hook}\n\nScript: ${post.script}\n\nCaption: ${post.caption}\n\nHashtags: ${post.hashtags}`, `${item.id}-post-${i}`)}
                  className="text-slate-500 hover:text-white text-xs flex items-center space-x-1"
                >
                  {copiedField === `${item.id}-post-${i}` ? <CheckCircle size={12} /> : <Copy size={12} />}
                  <span>Copy</span>
                </button>
              </div>
              <p className="text-white font-medium text-sm mb-1">{post.hook}</p>
              <p className="text-slate-400 text-sm line-clamp-2">{post.script?.substring(0, 100)}...</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Link in Bio card component
const LinkInBioCard = ({ item, expanded, onToggle, onCopy, copiedField, formatDate }) => {
  const links = item.content?.links || item.links || [];
  const bioText = item.content?.bio_text || item.bio_text || '';
  
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid={`link-in-bio-card-${item.id}`}>
      <button
        onClick={onToggle}
        className="w-full p-4 sm:p-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-3 mb-2 flex-wrap gap-2">
            <Link size={18} className="text-emerald-400 flex-shrink-0" weight="fill" />
            <h3 className="text-white font-semibold truncate">{item.title || `Link in Bio - ${item.content?.niche || item.niche || 'Unknown'}`}</h3>
            <TypeBadge type="Link in Bio" color="green" />
            <TypeBadge type={`${links.length} links`} color="cyan" />
          </div>
          <p className="text-slate-400 text-sm truncate">{bioText.substring(0, 60)}...</p>
          <p className="text-slate-500 text-xs mt-1">{formatDate(item.created_at)}</p>
        </div>
        <div className="flex items-center space-x-3 ml-4">
          {expanded ? <CaretUp size={20} className="text-slate-400" /> : <CaretDown size={20} className="text-slate-400" />}
        </div>
      </button>
      
      {expanded && (
        <div className="px-4 sm:px-5 pb-5 space-y-4 border-t border-white/10 pt-4">
          <ContentSection label="Bio Text" content={bioText} onCopy={onCopy} copiedField={copiedField} id={`${item.id}-bio`} highlight accentColor="green" />
          
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Links</label>
            <div className="space-y-2">
              {links.map((link, i) => (
                <div key={i} className="flex items-center justify-between bg-white/5 p-3 rounded-lg">
                  <div>
                    <p className="text-white text-sm font-medium">{link.text}</p>
                    <p className="text-emerald-400 text-xs">CTA: {link.cta}</p>
                  </div>
                  <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-xs">{link.url_type}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Business Plan card component
const BusinessPlanCard = ({ item, expanded, onToggle, onCopy, copiedField, formatDate }) => {
  const content = item.content || {};
  
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid={`business-plan-card-${item.id}`}>
      <button
        onClick={onToggle}
        className="w-full p-4 sm:p-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-3 mb-2 flex-wrap gap-2">
            <Target size={18} className="text-blue-400 flex-shrink-0" weight="fill" />
            <h3 className="text-white font-semibold truncate">{item.title || 'Business Blueprint'}</h3>
            <TypeBadge type="Business Plan" color="blue" />
          </div>
          <p className="text-slate-400 text-sm truncate">{content.niche || 'Your business strategy'}</p>
          <p className="text-slate-500 text-xs mt-1">{formatDate(item.created_at)}</p>
        </div>
        <div className="flex items-center space-x-3 ml-4">
          {expanded ? <CaretUp size={20} className="text-slate-400" /> : <CaretDown size={20} className="text-slate-400" />}
        </div>
      </button>
      
      {expanded && (
        <div className="px-4 sm:px-5 pb-5 space-y-4 border-t border-white/10 pt-4">
          {content.recommended_strategy && (
            <ContentSection label="Strategy" content={content.recommended_strategy} onCopy={onCopy} copiedField={copiedField} id={`${item.id}-strategy`} highlight accentColor="blue" />
          )}
          
          {content.monetization_path?.length > 0 && (
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Monetization Path</label>
              <div className="space-y-2">
                {content.monetization_path.map((phase, i) => (
                  <div key={i} className="bg-white/5 p-3 rounded-lg">
                    <p className="text-blue-400 font-medium text-sm">{phase.phase}</p>
                    <p className="text-slate-300 text-sm">{phase.action}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {content.next_steps?.length > 0 && (
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Next Steps</label>
              <ul className="space-y-2">
                {content.next_steps.map((step, i) => (
                  <li key={i} className="flex items-start space-x-2 text-slate-300 bg-white/5 p-3 rounded-lg text-sm">
                    <CheckCircle size={14} className="text-blue-400 mt-0.5 flex-shrink-0" weight="fill" />
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Ebook card — simple card for ebooks saved via ebook_service → my_creations
const EbookCard = ({ item, expanded, onToggle, onCopy, copiedField, formatDate }) => {
  const content = item.content || {};
  const title = item.title || content.title || 'Ebook';
  const topic = content.topic || item.topic || '';
  const tier = item.plan_tier || 'starter';
  const cover = content.cover_image || item.cover_image;

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid={`ebook-card-${item.id}`}>
      <div
        onClick={onToggle}
        className="w-full p-4 sm:p-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          {cover ? (
            <img src={cover} alt="" className="w-12 h-16 object-cover rounded flex-shrink-0" />
          ) : (
            <div className="w-12 h-16 bg-gradient-to-br from-indigo-500/40 to-purple-500/40 rounded flex-shrink-0 flex items-center justify-center">
              <Files size={20} className="text-white/80" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1 flex-wrap gap-2">
              <h3 className="text-white font-semibold truncate">{title}</h3>
              <TypeBadge type="Ebook" color="purple" />
              <TypeBadge type={tier} color={tier === 'pro' ? 'green' : tier === 'growth' ? 'cyan' : 'blue'} />
            </div>
            <p className="text-slate-400 text-sm truncate">{topic}</p>
            <p className="text-slate-500 text-xs mt-1">{formatDate(item.created_at)}</p>
          </div>
        </div>
        <div className="ml-2 sm:ml-4 flex items-center gap-2 flex-shrink-0">
          <DeliverableActions
            creationId={item.id}
            title={title}
            type="ebook"
            copyText={[title, topic, content.subtitle, ...((content.chapters || []).map(ch => typeof ch === 'string' ? ch : (ch.title || ch.heading)))].filter(Boolean).join('\n\n')}
            onView={(id) => window.dispatchEvent(new CustomEvent('paygbot:open-creation', { detail: id }))}
            onDeleted={() => window.dispatchEvent(new CustomEvent('paygbot:refresh-creations'))}
          />
          {expanded
            ? <CaretUp size={20} className="text-slate-400 hidden sm:block" />
            : <CaretDown size={20} className="text-slate-400 hidden sm:block" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 sm:px-5 pb-5 space-y-3 border-t border-white/10 pt-4">
          {content.subtitle && <p className="text-slate-300 text-sm italic">{content.subtitle}</p>}
          {content.tier_message && (
            <p className="text-purple-300 text-xs">{content.tier_message}</p>
          )}
          {Array.isArray(content.chapters) && content.chapters.length > 0 && (
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">
                Chapters
              </label>
              <ul className="space-y-1">
                {content.chapters.slice(0, 12).map((ch, i) => (
                  <li key={i} className="text-slate-300 text-sm bg-white/5 px-3 py-2 rounded">
                    {typeof ch === 'string' ? ch : (ch.title || ch.heading || `Chapter ${i + 1}`)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {content.ebook_id && (
            <button
              onClick={() => onCopy(content.ebook_id, `${item.id}-ebook-id`)}
              className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
            >
              {copiedField === `${item.id}-ebook-id` ? <CheckCircle size={12} /> : <Copy size={12} />}
              <span>Copy ebook ID</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};


// Round 81 — Visible diagnostic panel for the founder/internal user.
// Reads the SAME fields the production Ready badge / Send to Metricool
// gates read, and shows the value + decision side-by-side. Screenshot
// this from production to settle "why is this Ready but no voice?" or
// "why is Send to Metricool hidden?" in one frame, no logs needed.
const DfyVideoDiagnosticPanel = ({ item, content, playableUrl, videoError }) => {
  // Mirror of the merged-field lookup the Round 81 SendToMetricoolButton uses.
  const captionMerged = content.caption || item.caption || '';
  const scriptMerged = content.script || content.voiceover_script || item.script || '';
  const voicePreview = content.voice_preview || item.voice_preview || null;
  const audioUrlMerged =
    content.audio_url || content.voice_url || (voicePreview || {}).audio_url || item.audio_url || '';
  const audioDurationMerged =
    content.audio_duration || (voicePreview || {}).audio_duration || item.audio_duration || 0;
  const videoUrlMerged =
    content.video_url || content.media_url || item.video_url || playableUrl || '';
  const playableIsPublic =
    typeof playableUrl === 'string' && /^https?:\/\//i.test(playableUrl);

  // Mirror of the Round 81 Ready badge guard.
  let readyDecision;
  if (videoError) readyDecision = 'needs_repair (video element failed to load)';
  else if (!item.status) readyDecision = 'queued';
  else if (item.status === 'completed') {
    if (!playableUrl) readyDecision = 'needs_repair (no playable URL)';
    else if (content.voiceover_attached !== true) readyDecision = `needs_repair (voiceover_attached=${JSON.stringify(content.voiceover_attached)} ≠ true)`;
    else readyDecision = 'completed (Ready)';
  } else readyDecision = item.status;

  const metricoolSend = item.metricool_send || null;
  const tiktokFailure = (() => {
    if (!metricoolSend) return null;
    const details = metricoolSend.result?.details || [];
    const tt = details.find((d) =>
      String(d?.network || d?.platform || d?.networkLabel || '').toUpperCase().includes('TIKTOK'),
    );
    return tt || null;
  })();

  const Row = ({ label, value, ok }) => (
    <div className="flex items-start gap-2 text-xs leading-snug">
      <span
        className={`inline-block w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${
          ok === true ? 'bg-emerald-400' : ok === false ? 'bg-rose-400' : 'bg-slate-500'
        }`}
      />
      <span className="text-slate-400 w-44 flex-shrink-0">{label}</span>
      <span className={`font-mono break-all ${ok === false ? 'text-rose-300' : 'text-slate-200'}`}>
        {value === '' || value === null || value === undefined ? '∅' : String(value)}
      </span>
    </div>
  );

  return (
    <div
      data-testid={`dfy-video-diagnostic-${item.id}`}
      className="rounded-lg bg-slate-900/60 border border-slate-700 px-3 py-3 space-y-1.5"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">
          🔍 Founder Diagnostic
        </span>
        <span className="text-[10px] font-mono text-slate-500 break-all">id={item.id}</span>
      </div>
      <Row label="item.status" value={item.status} ok={item.status === 'completed' || item.status === 'ready'} />
      <Row label="job_status" value={item.job_status} />
      <Row
        label="voiceover_attached"
        value={JSON.stringify(content.voiceover_attached)}
        ok={content.voiceover_attached === true}
      />
      <Row
        label="composition_completed"
        value={JSON.stringify(content.composition_completed)}
        ok={content.composition_completed === true}
      />
      <Row label="audio_url" value={audioUrlMerged} ok={Boolean(audioUrlMerged)} />
      <Row label="audio_duration" value={audioDurationMerged} ok={Number(audioDurationMerged) > 0} />
      <Row label="voice_preview present" value={voicePreview ? 'yes' : 'no'} ok={Boolean(voicePreview)} />
      <Row label="video_url (merged)" value={videoUrlMerged} ok={Boolean(videoUrlMerged)} />
      <Row label="playableUrl" value={playableUrl} ok={Boolean(playableUrl)} />
      <Row label="playable URL public" value={playableIsPublic ? 'yes' : 'no'} ok={playableIsPublic} />
      <Row label="caption present" value={captionMerged ? `${captionMerged.slice(0, 60)}${captionMerged.length > 60 ? '…' : ''}` : '∅'} ok={Boolean(captionMerged.trim())} />
      <Row label="script present" value={scriptMerged ? 'yes' : 'no'} ok={Boolean(scriptMerged.trim())} />
      <Row label="platform" value={content.platform || item.platform} ok={Boolean(content.platform || item.platform)} />
      <Row label="content_type" value={content.content_type || item.content_type} />
      <div className="h-px bg-slate-700 my-2" />
      <Row
        label="→ Ready badge decision"
        value={readyDecision}
        ok={readyDecision.startsWith('completed')}
      />
      <Row
        label="→ Failure reason"
        value={content.failure_reason || '∅'}
      />
      {metricoolSend ? (
        <>
          <div className="h-px bg-slate-700 my-2" />
          <Row label="metricool.sent_at" value={metricoolSend.sent_at} />
          <Row label="metricool.platform" value={metricoolSend.platform} />
          <Row label="metricool.sent" value={metricoolSend.result?.sent} ok={Number(metricoolSend.result?.sent || 0) > 0} />
          <Row label="metricool.failed" value={metricoolSend.result?.failed} ok={Number(metricoolSend.result?.failed || 0) === 0} />
          {tiktokFailure && (
            <Row
              label="TikTok failure"
              value={tiktokFailure.error || tiktokFailure.message || JSON.stringify(tiktokFailure)}
              ok={false}
            />
          )}
          {!tiktokFailure && metricoolSend.result?.details?.length > 0 && (
            <Row
              label="metricool.first_detail"
              value={JSON.stringify(metricoolSend.result.details[0]).slice(0, 200)}
            />
          )}
        </>
      ) : (
        <Row label="metricool_send" value="never sent" />
      )}
    </div>
  );
};


// Round 71 — Send to Metricool button + modal for a finished DFY video.
// Shown only when the project is Ready AND has a public media URL +
// caption. Allows the user to override platform / content_type / caption
// / hashtags before pushing to Metricool. Gracefully blocks free/starter
// tiers via the backend's 403 (we surface the message via toast).
const SendToMetricoolButton = ({ item, playableUrl, copyableUrl, showHiddenReason = false }) => {
  const content = item.content || {};
  const alreadySent = Boolean(item.metricool_send?.sent_at);

  // Round 79 — Use the SHARED workflow next_action resolver instead of
  // re-implementing the readiness checks here. Folds the Round 71 Send
  // to Metricool button into the workflow's ready_to_approve state so
  // My Projects can never drift from Create Content / Weekly Planner.
  //
  // Round 81 — Older My Projects rows (pre-Round 76) store some of
  // these fields at the TOP level instead of under `content`. Read
  // from both so the workflow gate doesn't silently hide the button
  // on legacy rows. Plus: if the row has a real public video URL and
  // a caption, that's enough to send — render the button even when
  // the state machine can't classify the row (e.g. voice_preview
  // never stamped). Backend send-route enforces the strict gate.
  const playableIsPublic =
    typeof playableUrl === 'string' && /^https?:\/\//i.test(playableUrl);
  const captionMerged = content.caption || item.caption || '';
  const scriptMerged =
    content.script || content.voiceover_script || item.script || '';
  const voicePreviewMerged = content.voice_preview || item.voice_preview || null;
  const platformMerged =
    (content.platform || item.platform || '').toString();
  const contentTypeMerged =
    (content.content_type || item.content_type || 'reel').toString();
  const audioUrlMerged =
    content.audio_url ||
    content.voice_url ||
    voicePreviewMerged?.audio_url ||
    item.audio_url ||
    '';
  const audioDurationMerged =
    content.audio_duration ||
    voicePreviewMerged?.audio_duration ||
    item.audio_duration ||
    0;
  const videoUrlMerged =
    content.video_url ||
    content.media_url ||
    item.video_url ||
    playableUrl ||
    '';

  const workflowAsset = {
    script: scriptMerged,
    video_prompt: content.video_prompt || item.video_prompt,
    caption: captionMerged,
    platform: platformMerged,
    voice_preview: voicePreviewMerged,
    audio_url: audioUrlMerged,
    audio_duration: audioDurationMerged,
    video_status:
      item.job_status || (item.status === 'ready' ? 'completed' : item.status),
    video_url: videoUrlMerged,
    content_type: contentTypeMerged,
    media_type: item.media_type,
  };
  const workflow = getVideoNextAction(workflowAsset);
  const action = workflow.next_action;

  const [open, setOpen] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [sendDetails, setSendDetails] = React.useState(null);
  const [platform, setPlatform] = React.useState(
    (content.platform || item.platform || 'instagram').toLowerCase(),
  );
  const [contentType, setContentType] = React.useState(
    (content.content_type || item.content_type || 'reel').toLowerCase(),
  );
  const [caption, setCaption] = React.useState(content.caption || item.caption || '');
  const [hashtags, setHashtags] = React.useState(
    Array.isArray(content.hashtags || item.hashtags)
      ? (content.hashtags || item.hashtags).join(' ')
      : '',
  );

  // Round 81.2 — Track which fields started empty on the row so the
  // modal shows a "please fill these in" banner. Legacy DFY rows
  // routinely arrive without caption/platform/content_type stamped.
  const startedMissing = React.useMemo(() => {
    const missing = [];
    if (!(content.caption || item.caption || '').toString().trim()) missing.push('caption');
    if (!(content.platform || item.platform || '').toString().trim()) missing.push('platform');
    if (!(content.content_type || item.content_type || '').toString().trim()) missing.push('content type');
    return missing;
    // We intentionally only read the initial item snapshot here — this
    // is a one-shot "what did the row arrive with?" check, not live state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Map workflow states to the three legal UI states the spec demanded.
  // The shared resolver is the single source of truth — no custom
  // readiness checks beyond this point.
  if (action === NEXT_ACTION.GENERATING_VIDEO) {
    return (
      <div
        data-testid={`metricool-still-generating-${item.id}`}
        data-next-action={action}
        className="w-full py-2.5 bg-slate-700/40 text-slate-400 font-medium rounded-lg flex items-center justify-center gap-2 border border-slate-600/40"
      >
        <Spinner size={14} className="animate-spin" />
        <span>Still generating…</span>
      </div>
    );
  }
  if (action === NEXT_ACTION.VIDEO_FAILED) {
    return (
      <div
        data-testid={`metricool-needs-retry-${item.id}`}
        data-next-action={action}
        className="w-full py-2.5 bg-amber-500/15 text-amber-300 font-medium rounded-lg flex items-center justify-center gap-2 border border-amber-500/30"
      >
        <Warning size={14} weight="fill" />
        <span>Retry generation in Weekly Planner</span>
      </div>
    );
  }
  // Anything that's not yet "ready/approve" simply doesn't render the
  // Send button — Round 78 ensures the Generate/Preview CTA is shown
  // on Weekly Planner instead (My Projects is a backup send path,
  // not the primary generation surface).
  //
  // Round 81 — Legacy-row escape hatch. The shared workflow can't
  // classify a row whose voice_preview was never stamped (true for
  // every DFY video created before Round 76).
  //
  // Round 81.2 — Production diagnostic on 2026-05-26 revealed many
  // legacy rows have voiceover_attached=true + public URL but EMPTY
  // caption / platform / content_type on the DB row. Previously we
  // also required `captionMerged.trim()` here, which hid the button
  // even though the modal is perfectly capable of collecting the
  // missing caption + platform + content_type from the founder
  // before the actual send. New rule: a row qualifies as legacy-
  // sendable when the video file is real (public URL) AND has voice
  // attached (voiceover_attached === true OR audio_url present). The
  // modal then collects whatever's missing, and the backend's strict
  // gate enforces the final contract.
  if (action !== NEXT_ACTION.READY_TO_APPROVE && action !== NEXT_ACTION.VIDEO_READY) {
    const statusLooksReady =
      item.status === 'ready' || item.status === 'completed';
    const voiceAttachedToFile =
      content.voiceover_attached === true || Boolean(audioUrlMerged);
    const legacyCanSend =
      statusLooksReady && playableIsPublic && voiceAttachedToFile;
    if (!legacyCanSend) {
      // Round 81 — For internal/founder users, render a visible
      // placeholder explaining EXACTLY why the Send button is hidden.
      // Normal users still see nothing.
      if (showHiddenReason) {
        const hiddenReasons = [];
        if (!statusLooksReady) hiddenReasons.push(`status="${item.status || 'unknown'}"`);
        if (!playableIsPublic) hiddenReasons.push(`playable URL not public (${playableUrl ? playableUrl.slice(0, 60) : 'empty'})`);
        if (!voiceAttachedToFile) hiddenReasons.push('no voice on file (voiceover_attached !== true AND audio_url empty)');
        hiddenReasons.push(`workflow=${action}`);
        return (
          <div
            data-testid={`send-to-metricool-hidden-${item.id}`}
            className="w-full px-3 py-2.5 bg-amber-500/10 text-amber-200 text-xs font-mono rounded-lg border border-amber-500/30 leading-relaxed"
          >
            <div className="font-semibold mb-1">⚠ Send to Metricool hidden</div>
            <div className="text-amber-300/90">{hiddenReasons.join(' · ')}</div>
          </div>
        );
      }
      return null;
    }
  }

  const handleSend = async () => {
    if (sending) return;
    setSending(true);
    setSendDetails(null);
    const cleanedHashtags = hashtags
      .split(/\s+/)
      .map((h) => h.trim().replace(/^#/, ''))
      .filter(Boolean);
    try {
      // Persist edits first so the row reflects what was sent.
      await api.patch(`/my-creations/${item.id}/meta`, {
        caption,
        hashtags: cleanedHashtags,
        platform,
        content_type: contentType,
      });
      const res = await api.post(`/my-creations/${item.id}/send-to-metricool`, {
        platform,
        content_type: contentType,
        caption,
        hashtags: cleanedHashtags,
      });
      // Round 83 — Surface per-post details on BOTH success and partial
      // failure. Backend returns details: [{platform, error, error_message,
      // metricool_status, ...}]. Previously the modal swallowed these and
      // only showed a generic "Metricool push failed" toast, which left
      // the user with no way to know that TikTok was rejected because the
      // account wasn't connected (etc.). Now we keep them in modal state
      // and render them inline.
      const details = Array.isArray(res?.data?.details) ? res.data.details : [];
      if (res?.data?.success) {
        toast.success(`Sent to Metricool (${platform}). Scheduled at ${res.data.scheduled_time}.`);
        // Even on success, render any per-platform warnings (e.g. some
        // platforms in a multi-platform send may have failed).
        if (details.some((d) => d?.status === 'failed' || d?.error || d?.error_message)) {
          setSendDetails({ kind: 'partial', details });
        } else {
          setOpen(false);
        }
      } else {
        setSendDetails({
          kind: 'failed',
          error: res?.data?.error || 'Metricool push failed',
          details,
        });
        toast.error(res?.data?.error || 'Metricool push failed — see details in modal');
      }
    } catch (err) {
      const detailField = err?.response?.data?.detail;
      let msg;
      let parsedDetails = [];
      if (typeof detailField === 'string') {
        msg = detailField;
      } else if (detailField && typeof detailField === 'object') {
        msg = detailField.message || detailField.error || JSON.stringify(detailField);
        if (Array.isArray(detailField.details)) parsedDetails = detailField.details;
      } else {
        msg = err?.message || 'Failed to send to Metricool';
      }
      setSendDetails({ kind: 'failed', error: msg, details: parsedDetails });
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid={`send-to-metricool-btn-${item.id}`}
        className={`w-full py-2.5 font-medium rounded-lg flex items-center justify-center gap-2 transition-colors ${
          alreadySent
            ? 'bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 border border-emerald-500/40'
            : 'bg-fuchsia-500 hover:bg-fuchsia-600 text-white'
        }`}
      >
        <PaperPlaneTilt size={16} weight={alreadySent ? 'regular' : 'fill'} />
        <span>{alreadySent ? 'Send to Metricool again' : 'Send to Metricool'}</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            data-testid={`send-to-metricool-modal-${item.id}`}
            onClick={() => !sending && setOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-slate-900 border border-white/10 rounded-2xl p-6 w-full max-w-md space-y-4"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-white text-lg font-semibold flex items-center gap-2">
                  <ShareNetwork size={20} weight="fill" className="text-fuchsia-400" />
                  Send to Metricool
                </h3>
                <button onClick={() => !sending && setOpen(false)} className="text-slate-400 hover:text-white">
                  <X size={20} />
                </button>
              </div>
              <p className="text-xs text-slate-400">
                Schedules this finished video to your connected Metricool account.
                Default schedule is 5 minutes from now — edit the time in Metricool after sending.
              </p>

              {startedMissing.length > 0 && (
                <div
                  data-testid={`metricool-needs-input-banner-${item.id}`}
                  className="rounded-md bg-violet-500/10 border border-violet-500/30 px-3 py-2 text-violet-200 text-xs leading-relaxed"
                >
                  <span className="font-semibold">Heads up:</span> this video was saved without {' '}
                  <span className="font-mono">{startedMissing.join(', ')}</span>.
                  Pick the values below before sending — they'll be saved back to the row.
                </div>
              )}

              <div>
                <label className="text-xs text-slate-300 uppercase tracking-wider mb-1 block">Platform</label>
                <div className="grid grid-cols-3 gap-2">
                  {['instagram', 'facebook', 'tiktok'].map((p) => (
                    <button
                      key={p}
                      onClick={() => setPlatform(p)}
                      data-testid={`metricool-platform-${p}`}
                      className={`py-2 text-xs font-medium rounded-md capitalize transition-colors ${
                        platform === p
                          ? 'bg-fuchsia-500 text-white'
                          : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-300 uppercase tracking-wider mb-1 block">Content type</label>
                <div className="grid grid-cols-3 gap-2">
                  {['reel', 'post', 'story'].map((t) => (
                    <button
                      key={t}
                      onClick={() => setContentType(t)}
                      data-testid={`metricool-type-${t}`}
                      className={`py-2 text-xs font-medium rounded-md capitalize transition-colors ${
                        contentType === t
                          ? 'bg-fuchsia-500 text-white'
                          : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-300 uppercase tracking-wider mb-1 block">Caption</label>
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  data-testid="metricool-caption-input"
                  rows={3}
                  className="w-full bg-slate-800 border border-slate-700 rounded-md p-2 text-sm text-white"
                />
              </div>

              <div>
                <label className="text-xs text-slate-300 uppercase tracking-wider mb-1 block">
                  Hashtags <span className="text-slate-500">(space-separated, # optional)</span>
                </label>
                <input
                  value={hashtags}
                  onChange={(e) => setHashtags(e.target.value)}
                  data-testid="metricool-hashtags-input"
                  className="w-full bg-slate-800 border border-slate-700 rounded-md p-2 text-sm text-white"
                />
              </div>

              {sendDetails && (
                <div
                  data-testid={`metricool-send-result-${item.id}`}
                  className={`rounded-md px-3 py-2.5 text-xs leading-relaxed border ${
                    sendDetails.kind === 'failed'
                      ? 'bg-rose-500/10 border-rose-500/30 text-rose-200'
                      : 'bg-amber-500/10 border-amber-500/30 text-amber-200'
                  }`}
                >
                  <div className="font-semibold mb-1">
                    {sendDetails.kind === 'failed'
                      ? 'Send failed — exact reason from Metricool:'
                      : 'Partial success — some platforms returned an error:'}
                  </div>
                  {sendDetails.error && (
                    <div className="mb-2 font-mono text-[11px] break-words">
                      {sendDetails.error}
                    </div>
                  )}
                  {Array.isArray(sendDetails.details) && sendDetails.details.length > 0 && (
                    <div className="space-y-1.5 font-mono text-[11px]">
                      {sendDetails.details.map((d, i) => {
                        const plat = (d.platform || d.network || d.networkLabel || 'unknown').toString().toUpperCase();
                        const reason = d.error_message || d.error || d.metricool_status || JSON.stringify(d).slice(0, 200);
                        const ok = d.status === 'sent' || d.metricool_status === 'sent';
                        return (
                          <div key={i} className={`flex items-start gap-2 ${ok ? 'text-emerald-300' : ''}`}>
                            <span className="font-semibold w-20 flex-shrink-0">{plat}</span>
                            <span className="break-words">{ok ? '✓ scheduled' : reason}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={handleSend}
                disabled={sending || !caption.trim()}
                data-testid={`metricool-send-confirm-${item.id}`}
                className={`w-full py-2.5 font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors ${
                  sending || !caption.trim()
                    ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                    : 'bg-fuchsia-500 hover:bg-fuchsia-600 text-white'
                }`}
              >
                {sending ? (
                  <>
                    <Spinner size={16} className="animate-spin" />
                    <span>Sending…</span>
                  </>
                ) : (
                  <>
                    <PaperPlaneTilt size={16} weight="fill" />
                    <span>Send to Metricool</span>
                  </>
                )}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Local fallback for Warning icon (avoids a new import surface)
const Warning = ({ size = 14, weight, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={weight === 'fill' ? 'currentColor' : 'none'} className={className} stroke="currentColor" strokeWidth="2">
    <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
  </svg>
);


// 2026-05-29: Reuse a Ready DFY video in the Weekly Planner / Metricool
// Scheduler. Stores the content_id in sessionStorage and navigates to

// 2026-05-29: Founder-only Import Existing Video button — uploads a
// finished MP4 to PayGBot so it can be reused in Weekly Planner /
// Metricool. Never spends credits. Backend (`POST /api/videos/import`)
// runs ffprobe before persisting the row; failures surface as toast
// errors with a precise reason.
const ImportVideoButton = ({ onImported }) => {
  const inputRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);

  const handleClick = () => {
    if (uploading) return;
    inputRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-uploading the same file
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) {
      toast.error('File exceeds 200 MB limit.');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.mp4')) {
      toast.error('Only .mp4 uploads are supported.');
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const { videoImportAPI } = await import('@/utils/api');
      const title = file.name.replace(/\.mp4$/i, '').replace(/[_-]+/g, ' ');
      const res = await videoImportAPI.importFile(file, title, (evt) => {
        if (evt.total) {
          setProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      });
      toast.success(`Imported "${res.title}" (${res.duration_sec.toFixed(1)}s)`);
      if (onImported) onImported();
    } catch (err) {
      const det = err?.response?.data?.detail || {};
      toast.error(
        typeof det === 'string'
          ? det
          : (det.message || det.reason || 'Import failed'),
      );
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="video/mp4,.mp4"
        className="hidden"
        onChange={handleFile}
        data-testid="my-projects-import-video-file-input"
      />
      <button
        onClick={handleClick}
        disabled={uploading}
        className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-emerald-400/40 text-emerald-300 hover:border-emerald-300 hover:text-emerald-200 disabled:opacity-60 transition-colors"
        data-testid="my-projects-import-video-btn"
      >
        <PaperPlaneTilt size={16} />
        <span>{uploading ? `Uploading… ${progress}%` : 'Import Video'}</span>
      </button>
    </>
  );
};


// /scheduler — the page reads it and offers an immediate attach action.
// NEVER generates new video.
const UseInWeeklyPlannerButton = ({ item }) => {
  const [busy, setBusy] = React.useState(false);
  const navigate = useNavigate();

  const handleClick = async () => {
    setBusy(true);
    try {
      sessionStorage.setItem('weeklyPlanner.attachVideoContentId', item.id);
      navigate('/scheduler?attach=' + encodeURIComponent(item.id));
      toast.success('Open Weekly Planner to attach this video to a draft.');
    } catch (err) {
      toast.error('Could not open Weekly Planner');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={busy}
      className="flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-medium border border-slate-600 text-slate-100 hover:border-emerald-400 hover:text-emerald-300 transition-all"
      data-testid={`use-in-weekly-planner-btn-${item.id}`}
    >
      <PaperPlaneTilt size={16} />
      <span>{busy ? 'Opening…' : 'Use in Weekly Planner'}</span>
    </button>
  );
};



// DFY Video card component
const DfyVideoCard = ({
  item,
  expanded,
  onToggle,
  onCopy,
  copiedField,
  formatDate,
  isInternalAccount = false,
  // Phase 4.2 — founder-only "Retry with v2" wiring. All three props
  // are optional; when absent (older callers) the button doesn't render
  // and behavior is identical to pre-Phase-4.2.
  v2EnvAvailable = false,
  onRetryV2 = null,
  retryV2JobId = null,
  retryV2SourceId = null,
  // 2026-05-31 — User-facing delete with confirmation.
  onDelete = null,
}) => {
  const content = item.content || {};
  const storedVideoUrl = content.video_url;
  const storedThumbnailUrl = content.thumbnail_url;
  const [videoError, setVideoError] = React.useState(false);
  const [videoLoading, setVideoLoading] = React.useState(true);
  const [thumbnailError, setThumbnailError] = React.useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const videoRef = React.useRef(null);
  
  // Normalize video URL.
  // - If it's a locally-served `dfy_*.mp4` (legacy) OR `v2_final_*.mp4`
  //   / `v2_video_*.mp4` (Video Engine v2) file, route it through the
  //   current environment's `/api/media/videos` endpoint so the
  //   correct Cloudfront/preview URL is used.
  // - 2026-05-30: persistent Object Storage URLs (`/api/media/objstore/...`)
  //   are passed through with the backend origin prefixed so the
  //   browser plays directly from the objstore serve route.
  // - Otherwise (Cloudfront, S3, any external HTTPS URL the queue
  //   mirrored over from Runway), pass the URL through unchanged so
  //   the <video> tag can play it directly.
  const getNormalizedVideoUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Persistent Object Storage path — preserve the full path-with-slashes.
    if (url.includes('/api/media/objstore/')) {
      const idx = url.indexOf('/api/media/objstore/');
      return `${baseUrl}${url.substring(idx)}`;
    }
    const filename = url.split('/').pop().split('?')[0]; // strip query/JWT
    const isLocalManaged =
      filename.endsWith('.mp4')
      && (
        filename.startsWith('dfy_')
        || filename.startsWith('v2_final_')
        || filename.startsWith('v2_video_')
        // 2026-05-29: Import-Existing-Video uploads. Backend serves
        // them through the same `/api/media/videos/{filename}` route
        // after the `imported_` prefix was whitelisted in
        // `routers/videos.py`.
        || filename.startsWith('imported_')
      );
    if (isLocalManaged) {
      return `${baseUrl}/api/media/videos/${filename}`;
    }
    // External (Runway/Cloudfront/S3/etc.) — return the original URL.
    if (/^https?:\/\//i.test(url)) return url;
    return null;
  };

  // Normalize thumbnail URL — only for the locally-served dfy_*.mp4
  // case. v2 videos do not currently have generated thumbnails (the
  // <video> element falls back to its first frame, same as the
  // pre-thumbnail-pipeline legacy behaviour).
  const getNormalizedThumbnailUrl = (videoUrl) => {
    if (!videoUrl || typeof videoUrl !== 'string') return null;
    const filename = videoUrl.split('/').pop().split('?')[0];
    if (!filename.startsWith('dfy_') || !filename.endsWith('.mp4')) return null;
    const videoId = filename.replace('.mp4', '');
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${baseUrl}/api/media/thumbnails/thumbnail_${videoId}.jpg`;
  };
  
  const videoUrl = getNormalizedVideoUrl(storedVideoUrl);
  const thumbnailUrl = storedThumbnailUrl 
    ? getNormalizedThumbnailUrl(storedVideoUrl) 
    : getNormalizedThumbnailUrl(storedVideoUrl);
  
  // Reset state when expanding
  React.useEffect(() => {
    if (expanded) {
      setVideoError(false);
      setVideoLoading(true);
    }
  }, [expanded]);

  // Round 65 (2026-02) — Defensive watchdog. iPhone Safari sometimes
  // never fires `loadedmetadata` / `canplay` for inline `<video>`
  // tags when the network is slow OR when an upstream proxy strips
  // the Content-Length header. Without a watchdog the preview hangs
  // forever on the spinner even though the video is fully loaded
  // (the user can scrub it via the native controls but the spinner
  // overlay is still on top, blocking taps).
  //
  // The watchdog inspects `videoRef.current.readyState` after a few
  // seconds and clears the loading state if the element is already
  // capable of rendering a frame. This is the canonical workaround
  // documented by Apple for the "loadedmetadata-never-fires" quirk.
  React.useEffect(() => {
    if (!expanded || !videoLoading) return undefined;
    const timer = setTimeout(() => {
      const v = videoRef.current;
      // readyState >= 1 means HAVE_METADATA — we already know the
      // size, duration, etc. The element is functionally "loaded".
      if (v && v.readyState >= 1) {
        setVideoLoading(false);
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [expanded, videoLoading]);
  
  // Choose the URL we want users to open / save / copy. `getNormalizedVideoUrl`
  // already does the right thing for both cases:
  //   • dfy_*.mp4 filenames (locally hosted, even if a stale BACKEND_PUBLIC_URL
  //     baked the wrong host into the DB) → rewritten to ${REACT_APP_BACKEND_URL}
  //   • external CloudFront/S3 URLs → returned untouched, JWT preserved
  // So `playableUrl` should just be the normalized URL.
  const isExternal = (u) => typeof u === 'string' && /^https?:\/\//i.test(u);

  const playableUrl = videoUrl;

  // True only when the underlying file is the raw Runway artifact (silent).
  // Used purely for labelling — never for URL routing.
  const isRunwayArtifact = (u) => typeof u === 'string' && /cloudfront\.net\/.+\?_jwt=/.test(u);

  // Build the URL the "Open & Save" button hits. For local dfy_*.mp4 files we
  // route through the /api/videos/save/<filename> proxy that adds
  // Content-Disposition. For external CloudFront URLs (silent recovered
  // Build the URL the "Open & Save" button hits. Round 62 — for local
  // dfy_*.mp4 files we hit the same `/api/media/videos/{filename}`
  // endpoint as the inline preview, but with `?dl=1` so the backend
  // flips Content-Disposition from inline → attachment. This gives the
  // browser a clean Save Video / Downloads experience without breaking
  // the inline <video> preview that uses the un-queried URL.
  const getSaveToPhoneUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Persistent Object Storage — append ?dl=1 for attachment disposition.
    if (url.includes('/api/media/objstore/')) {
      const idx = url.indexOf('/api/media/objstore/');
      return `${baseUrl}${url.substring(idx)}?dl=1`;
    }
    const filename = url.split('/').pop().split('?')[0];
    const isLocalManaged =
      filename.endsWith('.mp4')
      && (
        filename.startsWith('dfy_')
        || filename.startsWith('v2_final_')
        || filename.startsWith('v2_video_')
        || filename.startsWith('imported_')
      );
    if (isLocalManaged) {
      return `${baseUrl}/api/media/videos/${filename}?dl=1`;
    }
    if (isExternal(url)) return url; // raw external (Runway/S3) — pass through
    return null;
  };

  // "Open MP4" direct-link URL. For local files this is the bare
  // inline URL (no ?dl=1) so the browser plays the mp4 natively.
  // For external URLs it's just the URL.
  const getOpenMp4Url = (url) => {
    if (!url || typeof url !== 'string') return null;
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Persistent Object Storage — direct path, no rewriting.
    if (url.includes('/api/media/objstore/')) {
      const idx = url.indexOf('/api/media/objstore/');
      return `${baseUrl}${url.substring(idx)}`;
    }
    const filename = url.split('/').pop().split('?')[0];
    const isLocalManaged =
      filename.endsWith('.mp4')
      && (
        filename.startsWith('dfy_')
        || filename.startsWith('v2_final_')
        || filename.startsWith('v2_video_')
        || filename.startsWith('imported_')
      );
    if (isLocalManaged) {
      return `${baseUrl}/api/media/videos/${filename}`;
    }
    if (isExternal(url)) return url;
    return null;
  };

  // Friendly label for hosting source (used in card body).
  const sourceLabel = isRunwayArtifact(storedVideoUrl)
    ? 'Silent video — voiceover not attached'
    : (storedVideoUrl ? 'PayGBot hosted video' : null);

  // Round 60 — Pre-flight HEAD probe on every render. If the stored
  // video URL doesn't resolve to a real video on this backend, the
  // card downgrades to "Needs Repair" immediately — even when the
  // DB still says completed. This is the front-line guard that
  // closes the "fake Ready card" loophole if any backend row is
  // ever in an inconsistent state.
  React.useEffect(() => {
    if (!playableUrl) {
      setVideoError(true);
      return;
    }
    let cancelled = false;
    const saveUrl = (() => {
      try {
        const filename = playableUrl.split('/').pop().split('?')[0];
        if (filename.startsWith('dfy_') && filename.endsWith('.mp4')) {
          const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
          return `${baseUrl}/api/media/videos/${filename}`;
        }
        return playableUrl;
      } catch (_e) {
        return playableUrl;
      }
    })();
    (async () => {
      try {
        const res = await fetch(saveUrl, { method: 'HEAD', cache: 'no-store' });
        if (cancelled) return;
        const ct = (res.headers.get('content-type') || '').toLowerCase();
        if (!res.ok || (!ct.startsWith('video/') && !ct.startsWith('application/octet-stream'))) {
          setVideoError(true);
        } else {
          setVideoError(false);
        }
      } catch (_e) {
        if (!cancelled) setVideoError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [playableUrl]);

  const handleVideoError = (e) => {
    // Only log errors in development, don't immediately show error state
    // This allows for retry attempts and network fluctuations
    console.warn('Video load issue:', e?.target?.error?.message);
    // Delay error state to allow for retries
    setTimeout(() => {
      if (videoRef.current && videoRef.current.error) {
        setVideoLoading(false);
        setVideoError(true);
      }
    }, 2000);
  };

  const handleVideoLoaded = () => {
    void('Video loaded successfully');
    setVideoLoading(false);
    setVideoError(false);
  };

  // Debug log - only in development
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      void('DfyVideoCard render - storedUrl:', storedVideoUrl, 'normalizedUrl:', videoUrl, 'playableUrl:', playableUrl);
    }
  }, [storedVideoUrl, videoUrl, playableUrl]);

  const handleSaveToPhone = async (e) => {
    e.preventDefault();
    const saveUrl = getSaveToPhoneUrl(playableUrl);
    if (!saveUrl) {
      toast.error('Video file missing — repair required.');
      return;
    }

    // Pre-flight HEAD: confirm the URL actually serves a video before we
    // open OR fetch it. Catches stale DB rows pointing at deleted files
    // and avoids ever showing the user a "Video not found" JSON page.
    try {
      const res = await fetch(saveUrl, { method: 'HEAD', cache: 'no-store' });
      if (!res.ok) {
        toast.error('Video file missing — repair required.');
        return;
      }
      const ct = res.headers.get('content-type') || '';
      if (!ct.startsWith('video/')) {
        toast.error('Video file missing — repair required.');
        return;
      }
    } catch {
      toast.error('Could not reach the video. Try again or repair this video.');
      return;
    }

    const ios = isIOS();

    // Round 63 (2026-02) — iOS "Save Video" requires a FILE share.
    // Background: `navigator.share({ url })` makes iOS treat the share
    // as a LINK, so the Share Sheet only shows Copy / Reading List /
    // Quick Note. To get the "Save Video" / "Save to Camera Roll"
    // action, we must call `navigator.share({ files: [File] })` with
    // a real `File` whose mime is `video/mp4`. iOS 15+ detects the
    // video file and exposes the camera-roll save action automatically.
    //
    // We try file-share first (the only path that reliably produces
    // Save Video). If `canShare({ files })` is false (older iOS Safari
    // or Web Share blocked), we fall back to the legacy URL share.
    // Final fallback is same-tab navigation so the user can long-press
    // → Save Video manually.
    if (ios) {
      // 1. Try file-share first. Fetch up to ~25MB inline; PayGBot DFY
      //    videos are typically 1-5MB so this is fine.
      try {
        const toastId = toast.loading('Preparing video for Save…');
        const res = await fetch(saveUrl, { method: 'GET', cache: 'no-store' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        toast.dismiss(toastId);
        if (!blob || !blob.type.startsWith('video/')) {
          throw new Error('blob is not video');
        }
        // Force the user-visible filename to be paygbot-video.mp4 so
        // the Share Sheet preview reads cleanly and the saved file in
        // the camera roll has a clean name (not dfy_<uuid>.mp4).
        const file = new File([blob], 'paygbot-video.mp4', { type: 'video/mp4' });
        if (
          typeof navigator !== 'undefined'
          && typeof navigator.canShare === 'function'
          && navigator.canShare({ files: [file] })
          && typeof navigator.share === 'function'
        ) {
          await navigator.share({
            files: [file],
            title: 'PayGBot Video',
            // NOTE: do NOT pass `url:` here — iOS will show the host name
            // instead of treating the share as a file, and Save Video
            // disappears from the sheet. Files-only share is the path
            // that exposes Save Video → Camera Roll.
          });
          return; // user picked Save Video / Save to Files / Cancel
        }
      } catch (err) {
        if (err && err.name === 'AbortError') return; // user cancelled
        // Otherwise fall through to the URL-share / nav fallback.
        console.warn('[Save Video] file-share fallback', err);
      }

      // 2. File-share unavailable → URL share (older iOS).
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        try {
          await navigator.share({ title: 'PayGBot Video', url: saveUrl });
          return;
        } catch (err) {
          if (err && err.name === 'AbortError') return;
        }
      }

      // 3. Last resort — same-tab navigation. Safari renders the mp4
      //    inline; user long-presses → Save Video to camera roll.
      window.location.href = saveUrl;
      return;
    }

    // Desktop path — fetch as blob, then trigger anchor download via the
    // shared helper. NEVER window.open().
    const filename = 'paygbot-video.mp4';
    try {
      const res = await fetch(saveUrl, { method: 'GET', cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      if (!blob || !blob.type.startsWith('video/')) {
        throw new Error('Not a video');
      }
      await downloadBlob(blob, filename);
      toast.success('Downloaded to Files / Downloads.', { duration: 4000 });
    } catch {
      // Fetch blocked (CORS / network). Same-tab navigation is the
      // safest fallback — the backend sets Content-Disposition: attachment
      // so desktop browsers will start a download instead of rendering.
      window.location.href = saveUrl;
    }
  };

  // The URL we want users to copy / share — Round 62 always the bare
  // inline MP4 URL on this backend (no ?dl=1). This is the direct
  // playable file: <video> can play it inline, iOS Safari treats it
  // as savable media in the Share sheet, and copying it gives the
  // customer a real .mp4 link, NOT an app page route.
  const copyableUrl = (() => {
    if (!playableUrl) return null;
    const direct = getOpenMp4Url(playableUrl);
    return direct || playableUrl;
  })();
  
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid={`dfy-video-card-${item.id}`}>
      <button
        onClick={onToggle}
        className="w-full p-4 sm:p-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-3 mb-2 flex-wrap gap-2">
            <VideoCamera size={18} className="text-fuchsia-400 flex-shrink-0" weight="fill" />
            <h3 className="text-white font-semibold truncate">{item.title || 'Generated Video'}</h3>
            <TypeBadge type="DFY Video" color="purple" />
            {/* Phase 4.2 — explicit engine origin badge so the founder
                can identify which pipeline produced this video at a
                glance. v2 = the Video Engine v2 orchestrator (Phase 3+);
                legacy origin gets no badge (default state). */}
            {content.engine === 'v2' && (
              <span
                data-testid={`video-engine-v2-badge-${item.id}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-200 text-[10px] font-semibold uppercase tracking-wider"
                title="Generated by Video Engine v2 (Phase 3+)"
              >
                engine: v2
              </span>
            )}
            {/* Voiced / Silent indicator. Only "Ready" videos earn the green
                Voiced pill; anything else is honestly labelled. */}
            {content.voiceover_attached && content.composition_completed ? (
              <span
                data-testid="voiced-badge"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px] font-semibold uppercase tracking-wider"
                title="Voiced final video"
              >
                <SpeakerHigh size={10} weight="fill" />
                Voiced
              </span>
            ) : (
              <span
                data-testid="silent-badge"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[10px] font-semibold uppercase tracking-wider"
                title="Silent video — voiceover not attached"
              >
                <SpeakerSlash size={10} weight="fill" />
                Silent
              </span>
            )}
          </div>
          <p className="text-slate-400 text-sm truncate">{content.script?.substring(0, 60) || 'Video with captions'}...</p>
          <p className="text-slate-500 text-xs mt-1">{formatDate(item.created_at)}</p>
        </div>
        <div className="flex items-center space-x-3 ml-4">
          <StatusBadge
            status={(() => {
              // Round 60 — Trust the DB status. Backend already gates
              // status="completed" on a real, non-empty mp4 + voiceover
              // (services/video_validation.py wired into every
              // update_status path). If the <video> element on this card
              // failed to load, downgrade to needs_repair so the user
              // sees the truth.
              //
              // Round 81 — TIGHTENED voiceover guard. Previously this
              // used `content.voiceover_attached === false`, which only
              // tripped when the field was explicitly `false`. Old rows
              // where the field is missing/`null`/`undefined` (which
              // happened for every DFY video pre-Round 60) sneaked
              // through as "Ready" even though no voice was attached.
              // We now require an explicit `=== true`; anything else
              // downgrades to needs_repair.
              if (videoError) return 'needs_repair';
              if (!item.status) return 'queued';
              if (item.status === 'completed') {
                if (!playableUrl) return 'needs_repair';
                // 2026-05-29: Imported videos bypass the
                // `voiceover_attached === true` guard. They're
                // finished assets the founder uploaded directly —
                // they're ready as-is whether or not they carry an
                // ElevenLabs voiceover. The "Silent" badge still
                // surfaces when the file has no audio stream, which
                // is informational, not a defect.
                if (item.source === 'imported') return 'completed';
                if (content.voiceover_attached !== true) return 'needs_repair';
                return 'completed';
              }
              return item.status;
            })()}
          />
          {expanded ? <CaretUp size={20} className="text-slate-400" /> : <CaretDown size={20} className="text-slate-400" />}
        </div>
      </button>
      
      {expanded && (
        <div className="px-4 sm:px-5 pb-5 space-y-4 border-t border-white/10 pt-4">
          {/* Source / state label */}
          {sourceLabel && (
            <p className="text-xs text-slate-500 italic" data-testid="video-source-label">{sourceLabel}</p>
          )}

          {/* Failure reason banner (only visible when something went wrong) */}
          {content.failure_reason && (
            <div
              className="rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-amber-200 text-sm"
              data-testid="video-failure-reason"
            >
              <strong className="font-semibold">Needs repair:</strong> {String(content.failure_reason).replace(/_/g, ' ')}.
            </div>
          )}

          {/* Phase 4.2 — Founder-only "Retry with v2" panel.
              Visible iff:
                • caller is founder/internal
                • backend env flag is ON (v2EnvAvailable)
                • row has enough stored content (script + video_prompt)
                  to rebuild a video — the backend re-checks this.
              Failure mode: backend returns 400/403/404/409 → toast
              from `handleRetryWithV2` explains what's wrong. */}
          {isInternalAccount
            && typeof onRetryV2 === 'function'
            && (content.script || item.script)
            && (content.video_prompt || item.video_prompt) && (
            <div
              data-testid={`retry-v2-panel-${item.id}`}
              className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-3 space-y-2"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold text-amber-200 uppercase tracking-wider">
                    Founder · retry with v2
                  </p>
                  <p className="text-xs text-amber-100/80 mt-0.5">
                    Rebuild this video through the Video Engine v2 pipeline
                    (ElevenLabs voice → Runway gen4.5 → ffmpeg mux → ffprobe gate).
                    {' '}A new row is saved on success; the current row is preserved.
                  </p>
                </div>
                <button
                  type="button"
                  data-testid={`retry-with-v2-btn-${item.id}`}
                  disabled={!v2EnvAvailable || (retryV2JobId && retryV2SourceId === item.id)}
                  onClick={() => onRetryV2(item)}
                  className={`px-3 py-2 text-xs font-semibold rounded-md border whitespace-nowrap transition-all flex-shrink-0 ${
                    !v2EnvAvailable
                      ? 'border-slate-700 bg-slate-800/60 text-slate-500 cursor-not-allowed'
                      : (retryV2JobId && retryV2SourceId === item.id)
                        ? 'border-amber-500/40 bg-amber-500/15 text-amber-200 cursor-wait'
                        : 'border-amber-500/50 bg-amber-500/15 text-amber-100 hover:bg-amber-500/25 hover:border-amber-400/70'
                  }`}
                  title={
                    !v2EnvAvailable
                      ? 'v2 retry disabled — set VIDEO_ENGINE_V2_REAL_PROVIDERS=1 on the backend and restart.'
                      : (retryV2JobId && retryV2SourceId === item.id)
                        ? 'v2 retry in progress…'
                        : 'Retry this video through Video Engine v2.'
                  }
                >
                  {retryV2JobId && retryV2SourceId === item.id
                    ? 'Retrying…'
                    : (!v2EnvAvailable ? 'v2 disabled' : 'Retry with v2')}
                </button>
              </div>
              {!v2EnvAvailable && (
                <p className="text-[10px] font-mono text-amber-300/70" data-testid={`retry-v2-disabled-${item.id}`}>
                  v2_env_enabled=false · set VIDEO_ENGINE_V2_REAL_PROVIDERS=1 on backend &amp; restart.
                </p>
              )}
            </div>
          )}

          {/* Round 81 — Founder-only diagnostic panel. Surfaces the
              exact field values the Ready badge / Send-to-Metricool /
              workflow gates are reading on THIS row, plus the
              decisions each gate is making. Hidden for normal users. */}
          {isInternalAccount && (
            <DfyVideoDiagnosticPanel
              item={item}
              content={content}
              playableUrl={playableUrl}
              videoError={videoError}
            />
          )}

          {/* Video Preview/Thumbnail */}
          {playableUrl && (
            <div className="space-y-3">
              {/* Round 218A Phase 2 P0 — never render the <video>
                  element when the media_status is anything other
                  than ready/completed. Swap in a MediaStatusTile
                  placeholder so the customer sees a clear state
                  instead of a broken player. Legacy items without
                  a media_status stamp still play normally. */}
              {!isMediaPlayable(item) ? (
                <MediaStatusTile item={item} />
              ) : (
              <div
                className="aspect-[9/16] max-h-[400px] bg-black rounded-lg overflow-hidden mx-auto relative"
                data-testid="dfy-video-preview"
              >
                {!videoError ? (
                  <>
                    {videoLoading && (
                      <div
                        className="absolute inset-0 flex items-center justify-center bg-slate-800/60 z-10 pointer-events-none"
                        data-testid="dfy-video-preview-spinner"
                      >
                        <div className="text-center">
                          <div className="w-8 h-8 border-2 border-fuchsia-400 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                          <p className="text-slate-400 text-sm">Loading preview...</p>
                        </div>
                      </div>
                    )}
                    <video
                      ref={videoRef}
                      src={playableUrl}
                      controls
                      controlsList="nodownload"
                      playsInline
                      // Legacy WebKit attribute — older iOS Safari still
                      // needs this lowercase non-camelCase name.
                      // eslint-disable-next-line react/no-unknown-property
                      webkit-playsinline="true"
                      preload="metadata"
                      poster={thumbnailUrl && !thumbnailError ? thumbnailUrl : undefined}
                      className="w-full h-full object-contain bg-black"
                      onError={() => {
                        setVideoError(true);
                        setVideoLoading(false);
                      }}
                      onLoadedMetadata={() => setVideoLoading(false)}
                      onLoadedData={() => setVideoLoading(false)}
                      onCanPlay={() => setVideoLoading(false)}
                      onWaiting={() => {
                        // Buffering mid-playback — do NOT bring the
                        // "Loading preview…" overlay back up. Native
                        // controls already show their own spinner.
                      }}
                    >
                      Your browser does not support inline video. Use the Save Video button below to play it.
                    </video>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-slate-900">
                    <div className="text-center p-4">
                      <VideoCamera size={48} className="text-fuchsia-400/60 mx-auto mb-3" />
                      <p className="text-slate-300 text-sm mb-2">Preview unavailable</p>
                      <p className="text-slate-500 text-xs">Use Save Video to download the file.</p>
                    </div>
                  </div>
                )}
              </div>
              )}
              
              {/* Action Buttons */}
              <div className="space-y-2">
                <button
                  onClick={handleSaveToPhone}
                  data-testid="download-video-btn"
                  className="w-full py-3 bg-fuchsia-500 text-white hover:bg-fuchsia-600 font-medium rounded-lg flex items-center justify-center space-x-2 transition-colors"
                >
                  <Download size={18} weight="bold" />
                  <span>{isIOS() ? 'Save Video' : 'Download Video'}</span>
                </button>
                <p className="text-xs text-slate-500 text-center px-2" data-testid="download-helper">
                  {isIOS()
                    ? 'Opens the iPhone Share sheet — pick Save Video.'
                    : 'Saves to your Files / Downloads folder.'}
                </p>
                {/* Round 62 — "Open MP4" fallback. Opens the raw inline
                    URL in a new tab so the customer always has a way
                    to reach the bare playable mp4 (right-click → Save
                    on desktop, long-press → Save Video on iOS Safari)
                    even if the platform Share sheet refuses to expose
                    the Save Video option. */}
                {getOpenMp4Url(playableUrl) && (
                  <a
                    href={getOpenMp4Url(playableUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="open-mp4-btn"
                    className="w-full py-2.5 bg-slate-700/50 text-slate-300 hover:bg-slate-700/70 font-medium rounded-lg flex items-center justify-center space-x-2 transition-colors"
                  >
                    <ArrowSquareOut size={16} />
                    <span>Open MP4</span>
                  </a>
                )}
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    if (!copyableUrl) {
                      toast.error('Video file missing — repair required.');
                      return;
                    }

                    // iPhone-first: open the native Share sheet so the
                    // user can send the link directly to Messages /
                    // WhatsApp / Mail / Notes / Camera Roll. Falls back
                    // to clipboard on AbortError (user cancel) or any
                    // browser without `navigator.share`.
                    const ios = isIOS();
                    if (ios && typeof navigator !== 'undefined'
                        && typeof navigator.share === 'function') {
                      try {
                        await navigator.share({
                          title: 'Your Video',
                          text: 'Watch the video I just made on PayGBot',
                          url: copyableUrl,
                        });
                        return; // user picked something / cancelled cleanly
                      } catch (err) {
                        if (err && err.name === 'AbortError') return;
                        // fall through to clipboard
                      }
                    }

                    try {
                      await navigator.clipboard.writeText(copyableUrl);
                      onCopy(`${item.id}-video-link`);
                    } catch {
                      toast.error('Could not copy link. Long-press to select instead.');
                    }
                  }}
                  data-testid="copy-link-btn"
                  className="w-full py-2.5 bg-slate-700/50 text-slate-300 hover:bg-slate-700/70 font-medium rounded-lg flex items-center justify-center space-x-2 transition-colors"
                >
                  {copiedField === `${item.id}-video-link` ? (
                    <>
                      <Check size={16} className="text-green-400" />
                      <span>Link Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy size={16} />
                      <span>{isIOS() ? 'Share Video Link' : 'Copy Video Link'}</span>
                    </>
                  )}
                </button>
                {/* Round 71 — Send to Metricool from My Projects */}
                <SendToMetricoolButton
                  item={item}
                  playableUrl={playableUrl}
                  copyableUrl={copyableUrl}
                  showHiddenReason={isInternalAccount}
                />
                {/* 2026-05-29 — Use this Ready video in Weekly Planner.
                    Only renders when status is completed/ready AND the
                    video is actually playable; clicking creates an
                    unscheduled draft on the planner side then deep-links. */}
                {(item.status === 'completed' || item.status === 'ready') && playableUrl && (
                  <UseInWeeklyPlannerButton item={item} />
                )}
                {/* 2026-05-31 — User-facing Delete (with confirmation).
                    Available on every card; clients can remove their own
                    projects; founder/admin can remove any. */}
                {onDelete && (
                  <button
                    type="button"
                    data-testid="delete-project-btn"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20 hover:text-red-200 transition-colors text-sm font-medium"
                    title="Delete this project"
                  >
                    <Trash size={16} />
                    <span>Delete</span>
                  </button>
                )}
              </div>
            </div>
          )}
          
          {!playableUrl && (
            <div
              className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg text-center space-y-3"
              data-testid="video-missing-state"
            >
              <div>
                <p className="text-amber-200 text-sm font-medium">Video file missing — repair required.</p>
                <p className="text-amber-300/70 text-xs mt-1">
                  The original mp4 is no longer reachable. Run the admin repair-voiceover endpoint or contact support.
                </p>
              </div>
              {onDelete && (
                <button
                  type="button"
                  data-testid="delete-project-btn-missing"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20 hover:text-red-200 transition-colors text-sm font-medium"
                >
                  <Trash size={16} />
                  <span>Delete this project</span>
                </button>
              )}
            </div>
          )}
          
          {/* Script */}
          {content.script && (
            <ContentSection 
              label="Script (Captions)" 
              content={content.script} 
              onCopy={onCopy} 
              copiedField={copiedField} 
              id={`${item.id}-script`} 
            />
          )}
          
          {/* Video Prompt */}
          {content.video_prompt && (
            <ContentSection 
              label="Video Prompt" 
              content={content.video_prompt} 
              onCopy={onCopy} 
              copiedField={copiedField} 
              id={`${item.id}-prompt`}
              highlight
              accentColor="purple"
            />
          )}
        </div>
      )}

      {/* 2026-05-31 — Delete confirmation modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          data-testid="delete-project-confirm-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isDeleting) {
              setShowDeleteConfirm(false);
            }
          }}
        >
          <div className="bg-slate-900 border border-red-500/30 rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <Trash size={20} className="text-red-400" weight="fill" />
              </div>
              <h3 className="text-white font-bold text-lg">Delete this project?</h3>
            </div>
            <p className="text-slate-300 text-sm mb-2">
              You're about to delete <span className="font-semibold text-white">"{item.title || 'this project'}"</span>.
            </p>
            <p className="text-slate-400 text-xs mb-5">
              This removes it from My Projects permanently. Persistent video files
              in object storage are kept in the bucket but no longer linked to your
              workspace. This cannot be undone.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                data-testid="delete-project-cancel-btn"
                disabled={isDeleting}
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2.5 rounded-lg border border-white/10 bg-white/5 text-slate-200 hover:bg-white/10 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="delete-project-confirm-btn"
                disabled={isDeleting}
                onClick={async () => {
                  if (!onDelete || isDeleting) return;
                  setIsDeleting(true);
                  try {
                    await onDelete(item.id, item.title);
                    setShowDeleteConfirm(false);
                  } finally {
                    setIsDeleting(false);
                  }
                }}
                className="flex-1 px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <Spinner size={16} className="animate-spin" />
                    <span>Deleting…</span>
                  </>
                ) : (
                  <>
                    <Trash size={16} />
                    <span>Yes, delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyCreationsPage;
